import React, { lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { Flame, ChevronDown, Sparkles } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { useMonthlyUpsell } from './MonthlyUpsellContext';
export function NewHero() {
  const {
    showUpsell
  } = useMonthlyUpsell();
  return <section className="relative min-h-screen flex items-start justify-center overflow-hidden bg-black" data-id="element-1468">
      {/* Subtle background image overlay */}
      <div className="absolute inset-0 z-0" style={{
      backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/jd3SqN6BZWRL2cNA4MSpd4/7eb88251-2f54-4ef3-ab64-463024e08157.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      opacity: 0.08
    }} data-id="element-1469" />
      {/* Enhanced atmospheric background with more depth */}
      <div className="absolute inset-0 bg-gradient-radial from-stone-900/80 via-black/90 to-black" data-id="element-1470" />

      {/* Larger, more dramatic candlelight glow spots */}
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-amber-600/15 rounded-full blur-3xl animate-flicker" data-id="element-1471" />
      <div className="absolute bottom-1/3 right-1/4 w-[600px] h-[600px] bg-amber-500/15 rounded-full blur-3xl animate-flicker-delayed" data-id="element-1472" />

      {/* Additional ambient glow for depth */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-600/5 rounded-full blur-3xl animate-pulse-slow" data-id="element-1473" />

      {/* Vignette for focus */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 pointer-events-none" data-id="element-1474" />

      {/* MONUMENTAL BACKGROUND LOGO - Your actual PSI logo as powerful presence */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 3,
      delay: 0.5
    }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{
      width: '100vh',
      height: '100vh' // Mobile default
    }} data-id="element-1475">
        {/* Responsive width override */}
        <style data-id="element-1476">{`
        @media (min-width: 768px) {
          .hero-logo-container {
            width: 180vh !important;
            height: 180vh !important;
          }
        }
      `}</style>
        {/* PSI Logo - Rotating slowly with perfect opacity */}
        <motion.div className="w-full h-full flex items-center justify-center hero-logo-container" animate={{
        rotate: [0, 360]
      }} transition={{
        duration: 180,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-1477">
          <img src="https://cdn.magicpatterns.com/uploads/jToXSpcV6pHGAAwW9hQchh/PeptideScienceInstituteLogo2-Transparent.png" alt="PSI Logo" className="w-full h-full object-contain" style={{
          opacity: 0.06,
          filter: 'blur(1px)'
        }} data-id="element-1478" />
        </motion.div>

        {/* Subtle glow effect around the logo */}
        <motion.div className="absolute inset-0 bg-gradient-radial from-purple-600/8 via-cyan-600/5 to-transparent blur-3xl" animate={{
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-1479" />
      </motion.div>

      {/* Enhanced floating candles - more of them with varied timing */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 0.8
    }} className="absolute top-32 left-16 hidden lg:flex flex-col items-center" data-id="element-1480">
        <Flame className="w-7 h-7 text-amber-500 animate-flicker" data-id="element-1481" />
        <div className="w-1 h-20 bg-gradient-to-b from-amber-900/60 to-transparent" data-id="element-1482" />
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 1.0
    }} className="absolute top-32 right-16 hidden lg:flex flex-col items-center" data-id="element-1483">
        <Flame className="w-7 h-7 text-amber-500 animate-flicker-delayed" data-id="element-1484" />
        <div className="w-1 h-20 bg-gradient-to-b from-amber-900/60 to-transparent" data-id="element-1485" />
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 0.9
    }} className="absolute top-1/3 left-32 hidden xl:flex flex-col items-center" data-id="element-1486">
        <Flame className="w-6 h-6 text-amber-500 animate-flicker" style={{
        animationDelay: '0.3s'
      }} data-id="element-1487" />
        <div className="w-1 h-16 bg-gradient-to-b from-amber-900/50 to-transparent" data-id="element-1488" />
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2,
      delay: 1.1
    }} className="absolute top-1/3 right-32 hidden xl:flex flex-col items-center" data-id="element-1489">
        <Flame className="w-6 h-6 text-amber-500 animate-flicker-delayed" style={{
        animationDelay: '0.5s'
      }} data-id="element-1490" />
        <div className="w-1 h-16 bg-gradient-to-b from-amber-900/50 to-transparent" data-id="element-1491" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center pt-4 sm:pt-6 md:pt-8 pb-12 sm:pb-16 md:pb-20" data-id="element-1492">
        {/* Main headline - DRAMATICALLY ENHANCED */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="mb-8 sm:mb-10" data-id="element-1493">
          {/* Pre-headline */}
          <motion.div className="mb-4 md:mb-5" data-id="element-1494">
            <p className="sm:text-xs tracking-[0.2em] uppercase text-[16px] text-[#89847A] font-[400]" style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            letterSpacing: '0.2em'
          }} data-id="element-1495">
              What If Almost Everything They Told You About Peptides, Health &
              Aging... Was Wrong?
            </p>
          </motion.div>

          {/* Divider between lead-in and headline */}
          <div className="flex justify-center mb-6 sm:mb-8" data-id="element-1496">
            <div className="w-20 h-px" style={{
            background: 'linear-gradient(90deg, transparent, #d97706, #fbbf24, #d97706, transparent)',
            boxShadow: '0 0 8px rgba(251, 191, 36, 0.3), 0 0 20px rgba(217, 119, 6, 0.15)'
          }} data-id="element-1497" />
          </div>

          <motion.h1 initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 1.2,
          type: 'spring',
          stiffness: 100
        }} className="font-bold leading-tight" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-1498">
            {/* Line 2: THE HOOK — dominant amber, same size for both lines */}
            <motion.div className="mb-2 md:mb-3" data-id="element-1499">
              <div className="inline-block font-extrabold leading-[1.45]" style={{
              color: '#fbbf24',
              textShadow: '0 0 40px rgba(251, 191, 36, 0.3), 0 2px 4px rgba(0,0,0,0.5)'
            }} data-id="element-1500">
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-6xl" data-id="element-1501">
                  Master the Secrets to
                </div>
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-6xl mt-1 sm:mt-2" style={{
                color: '#fbbf24',
                textShadow: '0 0 60px rgba(251, 191, 36, 0.5), 0 0 120px rgba(251, 191, 36, 0.2), 0 2px 4px rgba(0,0,0,0.5)'
              }} data-id="element-1502">
                  Control Human Biology
                </div>
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-6xl mt-3 sm:mt-4 md:mt-5 font-bold" style={{
                color: '#22d3ee',
                textShadow: '0 0 15px rgba(34, 211, 238, 0.15), 0 0 30px rgba(34, 211, 238, 0.08)'
              }} data-id="element-1503">
                  <span className="underline decoration-[1.5px] underline-offset-[10px]" style={{
                  textDecorationColor: 'rgba(34, 211, 238, 0.5)',
                  textDecorationStyle: 'solid'
                }} data-id="element-1504">
                    At the Highest Levels
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Sub-headline — value proposition */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.4
          }} className="mt-6 md:mt-8 max-w-2xl mx-auto" data-id="element-1505">
              {/* Lead-in with dividers */}
              <div className="flex items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4" data-id="element-1506">
                <div className="w-8 sm:w-12 h-px bg-gradient-to-r from-transparent to-stone-600/50" data-id="element-1507" />
                <p className="text-xs sm:text-sm md:text-base text-stone-400 text-center leading-relaxed" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1508">
                  From the World's #1 Authority on Peptide Science,{' '}
                  <span className="font-bold" style={{
                  color: '#22d3ee'
                }} data-id="element-1509">
                    Brenden Henry
                  </span>
                  <br data-id="element-1510" />
                  and "The Human MRI",{' '}
                  <span className="font-bold" style={{
                  color: '#22d3ee'
                }} data-id="element-1511">
                    Dr. Ali Mazloum
                  </span>
                </p>
                <div className="w-8 sm:w-12 h-px bg-gradient-to-l from-transparent to-stone-600/50" data-id="element-1512" />
              </div>

              {/* Centered lines */}
              <div className="text-center space-y-1.5 sm:space-y-2 text-base sm:text-lg md:text-xl text-stone-300" style={{
              fontFamily: "'Inter', system-ui, sans-serif"
            }} data-id="element-1513">
                <p data-id="element-1514">Reverse "Incurable" Diseases</p>
                <p data-id="element-1515">Supercharge Your Health & Performance</p>
                <p data-id="element-1516">Dramatically Extend Human Lifespan</p>
              </div>

              {/* Parenthetical — emphasized */}

              {/* Geometric divider */}
            </motion.div>

            {/* CTA Button — Show Me How */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 1.5
          }} className="mt-6 md:mt-8 text-center" data-id="element-1517">
              <button onClick={() => {
              const livestreamSection = document.querySelector('[data-experience-livestream]');
              if (livestreamSection) {
                livestreamSection.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_60px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 cursor-pointer min-h-[44px]" style={{
              fontFamily: "'Inter', system-ui, sans-serif"
            }} data-id="element-1518">
                Yes! Show Me How to Master Human Biology
              </button>
            </motion.div>

            {/* Scholarship statement with sparkle */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 1.6
          }} className="mt-6 md:mt-8 max-w-2xl mx-auto text-center relative" data-id="element-1519">
              {/* Sparkle particles */}
              <style data-id="element-1520">{`
                @keyframes sparkle-float {
                  0%, 100% { opacity: 0; transform: scale(0) translateY(0); }
                  15% { opacity: 1; transform: scale(1.2) translateY(-2px); }
                  50% { opacity: 0.8; transform: scale(1) translateY(-5px); }
                  85% { opacity: 0.4; transform: scale(0.6) translateY(-8px); }
                }
                @keyframes sparkle-twinkle {
                  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
                  25% { opacity: 1; transform: scale(1) rotate(45deg); }
                  50% { opacity: 0.5; transform: scale(0.7) rotate(90deg); }
                  75% { opacity: 0.9; transform: scale(1.1) rotate(135deg); }
                }
                .sparkle-dot {
                  position: absolute;
                  width: 3px;
                  height: 3px;
                  border-radius: 50%;
                  background: #fbbf24;
                  box-shadow: 0 0 4px rgba(251, 191, 36, 0.7), 0 0 10px rgba(251, 191, 36, 0.35);
                  pointer-events: none;
                  animation: sparkle-float 2.5s ease-in-out infinite;
                }
                .sparkle-dot-alt {
                  position: absolute;
                  width: 2px;
                  height: 2px;
                  border-radius: 50%;
                  background: #fde68a;
                  box-shadow: 0 0 3px rgba(253, 230, 138, 0.8), 0 0 8px rgba(251, 191, 36, 0.4);
                  pointer-events: none;
                  animation: sparkle-twinkle 3.5s ease-in-out infinite;
                }
              `}</style>
              {/* Left side sparkles */}
              <div className="sparkle-dot" style={{
              top: '5%',
              left: '3%',
              animationDelay: '0s'
            }} data-id="element-1521" />
              <div className="sparkle-dot-alt" style={{
              top: '25%',
              left: '1%',
              animationDelay: '0.8s'
            }} data-id="element-1522" />
              <div className="sparkle-dot" style={{
              top: '50%',
              left: '0%',
              animationDelay: '1.6s',
              width: '2px',
              height: '2px'
            }} data-id="element-1523" />
              <div className="sparkle-dot-alt" style={{
              top: '75%',
              left: '4%',
              animationDelay: '2.4s'
            }} data-id="element-1524" />
              <div className="sparkle-dot" style={{
              top: '90%',
              left: '8%',
              animationDelay: '0.3s',
              width: '4px',
              height: '4px'
            }} data-id="element-1525" />
              {/* Right side sparkles */}
              <div className="sparkle-dot" style={{
              top: '8%',
              right: '2%',
              animationDelay: '0.5s'
            }} data-id="element-1526" />
              <div className="sparkle-dot-alt" style={{
              top: '30%',
              right: '5%',
              animationDelay: '1.3s'
            }} data-id="element-1527" />
              <div className="sparkle-dot" style={{
              top: '55%',
              right: '1%',
              animationDelay: '2.1s',
              width: '2px',
              height: '2px'
            }} data-id="element-1528" />
              <div className="sparkle-dot-alt" style={{
              top: '70%',
              right: '7%',
              animationDelay: '0.9s'
            }} data-id="element-1529" />
              <div className="sparkle-dot" style={{
              top: '85%',
              right: '3%',
              animationDelay: '1.7s',
              width: '4px',
              height: '4px'
            }} data-id="element-1530" />
              {/* Top sparkles */}
              <div className="sparkle-dot-alt" style={{
              top: '-5%',
              left: '20%',
              animationDelay: '2.0s'
            }} data-id="element-1531" />
              <div className="sparkle-dot" style={{
              top: '-2%',
              left: '45%',
              animationDelay: '0.7s',
              width: '2px',
              height: '2px'
            }} data-id="element-1532" />
              <div className="sparkle-dot-alt" style={{
              top: '-5%',
              right: '22%',
              animationDelay: '1.4s'
            }} data-id="element-1533" />
              <div className="sparkle-dot" style={{
              top: '0%',
              right: '40%',
              animationDelay: '2.8s'
            }} data-id="element-1534" />
              {/* Bottom sparkles */}
              <div className="sparkle-dot" style={{
              top: '95%',
              left: '18%',
              animationDelay: '1.1s'
            }} data-id="element-1535" />
              <div className="sparkle-dot-alt" style={{
              top: '100%',
              left: '50%',
              animationDelay: '0.2s'
            }} data-id="element-1536" />
              <div className="sparkle-dot" style={{
              top: '95%',
              right: '15%',
              animationDelay: '1.9s',
              width: '2px',
              height: '2px'
            }} data-id="element-1537" />
              {/* Inner scattered sparkles */}
              <div className="sparkle-dot-alt" style={{
              top: '15%',
              left: '12%',
              animationDelay: '2.5s'
            }} data-id="element-1538" />
              <div className="sparkle-dot-alt" style={{
              top: '40%',
              right: '14%',
              animationDelay: '0.6s'
            }} data-id="element-1539" />
              <div className="sparkle-dot" style={{
              top: '65%',
              left: '10%',
              animationDelay: '1.8s',
              width: '2px',
              height: '2px'
            }} data-id="element-1540" />

              <p className="text-sm sm:text-base md:text-lg text-stone-400 leading-relaxed relative z-10 font-normal" style={{
              fontFamily: "'Inter', system-ui, sans-serif"
            }} data-id="element-1541">
                Plus, new members get a{' '}
                <span className="text-green-400 font-bold" data-id="element-1542">
                  $2,500 scholarship
                </span>{' '}
                to{' '}
                <span className="text-amber-400 font-bold" data-id="element-1543">
                  Peptide Mastery 2.0 -{' '}
                  <span className="text-green-400" data-id="element-1544">FREE</span>
                </span>
                <br data-id="element-1545" />
                <br data-id="element-1546" />
                <span className="text-stone-500 italic" data-id="element-1547">
                  (Certification Available)
                </span>
              </p>
            </motion.div>

            {/* Elegant gold divider */}
            <div className="flex justify-center mt-6 mb-2" data-id="element-1548">
              <div className="w-32 sm:w-48 h-px" style={{
              background: 'linear-gradient(90deg, transparent, #92400e, #d97706, #fbbf24, #d97706, #92400e, transparent)',
              boxShadow: '0 0 12px rgba(251, 191, 36, 0.4), 0 0 30px rgba(217, 119, 6, 0.2)'
            }} data-id="element-1549" />
            </div>

            {/* Wilfred A. Testimonial — duplicated above CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.5
          }} className="mt-8 md:mt-10 max-w-4xl mx-auto relative" data-id="element-1550">
              <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1551" />

              <div className="relative px-4 sm:px-8 py-2 sm:py-3" style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 'normal'
            }} data-id="element-1552">
                <div className="relative mb-4 sm:mb-6 max-w-3xl mx-auto" data-id="element-1553">
                  <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1554" />

                  <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1555">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1556" />

                    <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 font-normal" data-id="element-1557">
                      I{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1558">
                        100% would put my name behind
                      </span>{' '}
                      Ali & Brenden. I've had other health coaches in the past
                      (most are MD's), and I must say, Brenden & Ali are{' '}
                      <span className="text-white font-bold" data-id="element-1559">
                        FAR more in depth, systems-oriented, and rigorous
                      </span>{' '}
                      than anything I've ever seen. Also I did grab that
                      peptides course. It has given me the knowledge to turn
                      familys' health conditions around even when the medical
                      system has failed them. I owe this to the lessons I've
                      acquired from Ali & Brenden. In terms of level of positive
                      impact on my life, they are likely in the{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-1560">
                        top 5 most important people I've ever met
                      </span>{' '}
                      as they have{' '}
                      <span className="text-white font-bold" data-id="element-1561">
                        added many years to my life
                      </span>{' '}
                      and to those of my loved ones.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1562">
                  <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1563" />
                  <div className="flex items-center gap-3 sm:gap-4" data-id="element-1564">
                    <div className="relative flex-shrink-0" data-id="element-1565">
                      <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-1566" />
                      <img src="https://cdn.magicpatterns.com/uploads/8tpyPupU8CaQsAzF9stSbx/WilfredA-2.jpg" alt="Wilfred A." className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-3 border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.3)]" data-id="element-1567" />
                    </div>
                    <div className="text-left" data-id="element-1568">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-0.5" data-id="element-1569">
                        Wilfred A.
                      </p>
                      <p className="text-stone-500 text-sm font-normal" data-id="element-1570">
                        Peptide Mastery Owner & Transformation Services Client
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Trust bar */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 1.7
          }} className="mt-6 sm:mt-8 flex justify-center" data-id="element-1571">
              <div className="relative px-5 sm:px-8 py-3 sm:py-4 rounded-lg border border-amber-600/30 bg-amber-950/20" style={{
              boxShadow: '0 0 20px rgba(217, 119, 6, 0.1), inset 0 0 20px rgba(217, 119, 6, 0.05)'
            }} data-id="element-1572">
                <p className="relative text-xs sm:text-sm md:text-base tracking-[0.1em] sm:tracking-[0.15em] uppercase font-medium text-center leading-relaxed" style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                letterSpacing: '0.12em',
                color: '#d4a574',
                textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
              }} data-id="element-1573">
                  PSI Is Trusted By 1,000+ Doctors, Researchers, Biohackers,
                  Athletes, Health Coaches & Professionals Who Go{' '}
                  <span className="font-bold text-amber-400" style={{
                  textShadow: '0 0 15px rgba(251, 191, 36, 0.4)'
                }} data-id="element-1574">
                    BEYOND
                  </span>{' '}
                  Normal Limitations
                </p>
              </div>
            </motion.div>
          </motion.h1>

          {/* PRO Identity Section — secret society invitation */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.45
        }} className="mt-2 sm:mt-3 max-w-4xl mx-auto relative" data-id="element-1575">
            <div className="relative p-6 sm:p-8 md:p-10 space-y-[12px] pt-[2px] pb-[2px]" data-id="element-1576">
              {/* ENROLLMENT IS NOW OPEN badge */}

              {/* === Section divider — signals new beat === */}
              <div className="flex items-center justify-center gap-3 pt-2 sm:pt-3 pb-4 sm:pb-6" data-transition-lines data-id="element-1577">
                <div className="w-16 sm:w-24 h-px" style={{
                background: 'linear-gradient(90deg, transparent, #d97706, #fbbf24)'
              }} data-id="element-1578" />
                <div className="w-2 h-2 bg-amber-500/60 rotate-45" data-id="element-1579" />
                <div className="w-16 sm:w-24 h-px" style={{
                background: 'linear-gradient(90deg, #fbbf24, #d97706, transparent)'
              }} data-id="element-1580" />
              </div>

              {/* === "You already know" — emotional validation sequence === */}
              <div className="pb-4 sm:pb-6" data-id="element-1581">
                <div className="text-center max-w-3xl mx-auto space-y-5 sm:space-y-7" data-id="element-1582">
                  {/* Line 1: Medical system is broken — the opening declaration */}
                  <motion.h2 initial={{
                  opacity: 0,
                  y: 14
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6
                }} className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] font-bold leading-tight bg-clip-text text-transparent animate-gradient-shift-fast" style={{
                  fontFamily: 'Cinzel, serif',
                  backgroundImage: 'linear-gradient(90deg, #b45309, #d97706, #f59e0b, #fbbf24, #f59e0b, #d97706, #f59e0b, #fbbf24, #f59e0b, #d97706, #b45309)'
                }} data-id="element-1583">
                    You already know the medical system is broken.
                  </motion.h2>

                  {/* Parenthetical aside — whispered insider truth */}
                  <motion.p initial={{
                  opacity: 0,
                  y: 8
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.15
                }} className="text-base sm:text-lg md:text-xl italic text-stone-500 -mt-2 sm:-mt-3" style={{
                  fontFamily: "'Inter', system-ui, sans-serif"
                }} data-id="element-1584">
                    (It's actually{' '}
                    <span className="text-red-400 font-bold not-italic uppercase tracking-wide" style={{
                    textShadow: '0 0 20px rgba(248, 113, 113, 0.3)'
                  }} data-id="element-1585">
                      decades
                    </span>{' '}
                    behind.)
                  </motion.p>

                  {/* Line 2: Influencers are full of sh*t — the shared frustration */}
                  <motion.h2 initial={{
                  opacity: 0,
                  y: 12
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.25
                }} className="text-xl sm:text-2xl md:text-3xl lg:text-[2.25rem] font-bold leading-tight text-stone-300" style={{
                  fontFamily: 'Cinzel, serif'
                }} data-id="element-1586">
                    You already know the "influencers" and "experts" out there
                    are{' '}
                    <span className="text-white font-extrabold" style={{
                    textShadow: '0 0 25px rgba(255, 255, 255, 0.2), 0 0 50px rgba(255, 255, 255, 0.08)'
                  }} data-id="element-1587">
                      full of sh*t
                    </span>
                    .
                  </motion.h2>

                  {/* Line 3: The resolution — conversational bridge */}
                  <motion.p initial={{
                  opacity: 0,
                  y: 8
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.4
                }} className="text-base sm:text-lg md:text-xl text-[#A69B95] font-normal pt-1 pb-3 sm:pb-4" style={{
                  fontFamily: "'Inter', system-ui, sans-serif"
                }} data-id="element-1588">
                    And you already know that no normal doctor, supplement
                    protocol or YouTube video is going to give you{' '}
                    <span className="text-amber-400 font-bold" data-id="element-1589">
                      what you're looking for
                    </span>
                    .
                  </motion.p>
                </div>
              </div>

              {/* Downward pull — visual cue */}
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 2.5
            }} className="flex justify-center mb-8" data-id="element-1590">
                <div className="flex flex-col items-center gap-1" data-id="element-1591">
                  <div className="w-px h-6 bg-gradient-to-b from-transparent to-amber-500/40" data-id="element-1592" />
                  <div className="w-1.5 h-1.5 bg-amber-500/50 rotate-45" data-id="element-1593" />
                </div>
              </motion.div>

              {/* PRO Reveal — the transition line */}
              <motion.div initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 2.7
            }} className="text-center mb-10 sm:mb-14" data-id="element-1594">
                {/* Lead-in — smaller, quieter */}
                <motion.p initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5
              }} className="text-base sm:text-lg md:text-xl text-stone-400 mb-3 sm:mb-4" style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                letterSpacing: '0.03em'
              }} data-id="element-1595">
                  This is why we created
                </motion.p>

                {/* BIG reveal headline */}
                <motion.h2 initial={{
                opacity: 0,
                scale: 0.95
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.7,
                delay: 0.15
              }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight bg-clip-text text-transparent animate-gradient-shift-fast mb-2" style={{
                fontFamily: 'Cinzel, serif',
                backgroundImage: 'linear-gradient(90deg, #b45309, #d97706, #f59e0b, #fbbf24, #f59e0b, #d97706, #f59e0b, #fbbf24, #f59e0b, #d97706, #b45309)',
                filter: 'drop-shadow(0 0 30px rgba(217, 119, 6, 0.4))'
              }} data-id="element-1596">
                  The Private
                  <br data-id="element-1597" />
                  Research Order
                </motion.h2>

                {/* PRO badge */}
                <motion.div initial={{
                opacity: 0,
                y: 5
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: 0.3
              }} className="mb-8 sm:mb-10" data-id="element-1598">
                  <span className="inline-block text-sm sm:text-base tracking-[0.25em] uppercase text-amber-500/70 font-semibold" style={{
                  fontFamily: "'Inter', system-ui, sans-serif"
                }} data-id="element-1599">
                    ( PRO )
                  </span>
                </motion.div>

                {/* Manifesto — 3 beats */}
                <motion.div initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.4
              }} className="max-w-3xl mx-auto mb-8 sm:mb-10 space-y-8 sm:space-y-10 text-left" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1600">
                  {/* Beat 1: Geniuses of biomedicine — fiery conviction */}
                  <div className="space-y-4 text-base sm:text-lg md:text-xl text-stone-300 leading-relaxed" data-id="element-1601">
                    <p className="text-center text-xs sm:text-sm tracking-[0.2em] uppercase font-medium mb-2 py-2.5 sm:py-3 px-5 sm:px-8 mx-auto inline-flex items-center justify-center border border-cyan-500/20 rounded-sm text-cyan-300/90" style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    letterSpacing: '0.25em',
                    background: 'linear-gradient(135deg, rgba(8,145,178,0.08) 0%, rgba(0,0,0,0.4) 50%, rgba(8,145,178,0.08) 100%)',
                    boxShadow: '0 0 20px rgba(34, 211, 238, 0.06), inset 0 0 20px rgba(34, 211, 238, 0.03)'
                  }} data-id="element-1602">
                      The elite private membership to control human biology at
                      the highest levels.
                    </p>

                    {/* CTA Button below elite membership line */}
                    <div className="mt-8 sm:mt-10 mb-8 sm:mb-10 text-center" data-id="element-1603">
                      <button onClick={() => {
                      const livestreamSection = document.querySelector('[data-experience-livestream]');
                      if (livestreamSection) {
                        livestreamSection.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }
                    }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_60px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 cursor-pointer min-h-[44px]" style={{
                      fontFamily: "'Inter', system-ui, sans-serif"
                    }} data-id="element-1604">
                        Yes! Show Me the Private Research Order
                      </button>
                    </div>

                    <div className="flex items-center justify-center gap-3 mb-2" data-id="element-1605">
                      <div className="w-10 sm:w-16 h-px bg-gradient-to-r from-transparent to-amber-600/40" data-id="element-1606" />
                      <div className="w-1.5 h-1.5 bg-amber-500/50 rotate-45" data-id="element-1607" />
                      <div className="w-10 sm:w-16 h-px bg-gradient-to-l from-transparent to-amber-600/40" data-id="element-1608" />
                    </div>
                    <p data-id="element-1609">
                      <span className="text-amber-400 font-bold" data-id="element-1610">
                        Brenden Henry
                      </span>{' '}
                      is known as the world's #1 authority on peptide science.
                    </p>
                    <p data-id="element-1611">
                      <span className="text-amber-400 font-bold" data-id="element-1612">
                        Dr. Ali Mazloum
                      </span>{' '}
                      is known as the{' '}
                      <span className="text-stone-100 font-bold" data-id="element-1613">
                        "Human MRI."
                      </span>
                    </p>
                    <p data-id="element-1614">
                      They are both{' '}
                      <span className="text-stone-100 font-bold" data-id="element-1615">
                        geniuses of biomedicine
                      </span>
                      .
                    </p>
                    <p data-id="element-1616">
                      They are, in some ways, autistic savants who have figured
                      out how to use their superpowers for good.
                    </p>
                    <p data-id="element-1617">
                      They{' '}
                      <span className="text-white font-extrabold uppercase tracking-wide" data-id="element-1618">
                        relentlessly
                      </span>{' '}
                      search for truth. They are{' '}
                      <span className="text-white font-extrabold uppercase tracking-wide" data-id="element-1619">
                        ruthless
                      </span>{' '}
                      about accuracy. And they will rip apart any theory or idea
                      that has flaws. They{' '}
                      <span className="text-white font-extrabold uppercase tracking-wide" data-id="element-1620">
                        shred
                      </span>{' '}
                      through the BS and firmly determine what's{' '}
                      <span className="text-amber-400 font-extrabold uppercase tracking-wide" data-id="element-1621">
                        REAL
                      </span>
                      .
                    </p>
                  </div>

                  {/* The source — calm reassurance */}
                  <div className="space-y-5 text-base sm:text-lg md:text-xl text-stone-400 leading-relaxed" data-id="element-1622">
                    <div className="relative px-5 sm:px-7 py-5 sm:py-6 border border-amber-900/25 rounded-lg bg-amber-950/[0.08]" data-id="element-1623">
                      <div className="absolute top-0 left-4 sm:left-6 -translate-y-1/2" data-id="element-1624">
                        <div className="w-8 sm:w-10 h-px bg-gradient-to-r from-amber-600/50 to-transparent" data-id="element-1625" />
                      </div>
                      <p className="text-base sm:text-lg md:text-xl text-stone-300 leading-relaxed" data-id="element-1626">
                        The Private Research Order ultimately grants you access
                        to{' '}
                        <span className="text-stone-100 font-bold" data-id="element-1627">
                          TWO masters
                        </span>{' '}
                        of biomedicine, biohacking, peptides and human
                        transformation. We have come together to provide you
                        with the{' '}
                        <span className="text-amber-400 font-bold" data-id="element-1628">BEST</span>,
                        most{' '}
                        <span className="text-amber-400 font-bold" data-id="element-1629">
                          ACCURATE
                        </span>
                        , most{' '}
                        <span className="text-amber-400 font-bold" data-id="element-1630">
                          EFFECTIVE
                        </span>{' '}
                        information to heal, regenerate and enhance your
                        biology.
                      </p>

                      {/* Bold pricing contrast callout */}
                      <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t-2 border-amber-500/40" data-id="element-1631">
                        <p className="text-lg sm:text-xl md:text-2xl text-center leading-relaxed font-semibold text-stone-100" data-id="element-1632">
                          It normally costs{' '}
                          <span className="text-amber-400 font-extrabold" style={{
                          textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
                        }} data-id="element-1633">
                            $1,500
                          </span>{' '}
                          to access one hour of their time.
                        </p>
                        <p className="text-xl sm:text-2xl md:text-3xl text-center leading-relaxed font-bold text-stone-100 mt-3 sm:mt-4" data-id="element-1634">
                          As a member of the Private Research Order you get{' '}
                          <span className="text-amber-400 font-extrabold uppercase tracking-wide" style={{
                          textShadow: '0 0 25px rgba(251, 191, 36, 0.5)'
                        }} data-id="element-1635">
                            SO much more
                          </span>
                          ...
                        </p>
                        <p className="text-xl sm:text-2xl md:text-3xl text-center leading-relaxed font-bold mt-2 sm:mt-3" data-id="element-1636">
                          For a{' '}
                          <span className="text-green-400 font-extrabold uppercase tracking-wide" style={{
                          textShadow: '0 0 25px rgba(74, 222, 128, 0.4)'
                        }} data-id="element-1637">
                            FRACTION
                          </span>{' '}
                          of the price.
                        </p>
                      </div>
                    </div>
                    <p data-id="element-1638">
                      And we're doing this away from mainstream limitations and{' '}
                      <span className="text-stone-200 font-semibold" data-id="element-1639">
                        Big Pharma's influence
                      </span>
                      .
                    </p>

                    <p className="text-stone-300" data-id="element-1640">
                      So you can relax. You can stop worrying about all the
                      noise out there. You've found exactly what you need.{' '}
                      <span className="text-amber-400 font-bold" data-id="element-1641">
                        This is the source.
                      </span>
                    </p>
                  </div>

                  {/* Standalone closer */}
                  <p className="text-center text-lg sm:text-xl md:text-2xl text-stone-200 font-semibold italic pt-2" style={{
                  fontFamily: "'Inter', system-ui, sans-serif"
                }} data-id="element-1642">
                    You may never have to worry about your biology{' '}
                    <span className="text-amber-400 font-bold not-italic" data-id="element-1643">
                      again
                    </span>
                    .
                  </p>
                </motion.div>

                {/* "Experience it for yourself" — quiet invitation */}
                <div className="flex flex-col items-center gap-3 mb-2" data-experience-livestream data-id="element-1644">
                  <div className="w-px h-8 bg-gradient-to-b from-transparent via-stone-600/30 to-stone-500/50" data-id="element-1645" />
                  <div className="flex flex-col items-center gap-1 max-w-xl text-center" data-id="element-1646">
                    <p className="text-base sm:text-lg md:text-xl tracking-[0.12em] uppercase text-stone-300 font-semibold leading-relaxed" style={{
                    fontFamily: "'Inter', system-ui, sans-serif"
                  }} data-id="element-1647">
                      Take a look for yourself, and experience a{' '}
                      <span className="text-amber-400 font-bold" data-id="element-1648">PRO</span>{' '}
                      livestream.
                    </p>
                    <p className="text-base sm:text-lg md:text-xl tracking-[0.12em] uppercase text-amber-400/90 font-bold leading-relaxed mt-1" style={{
                    fontFamily: "'Inter', system-ui, sans-serif"
                  }} data-id="element-1649">
                      You will see the depth that's covered
                    </p>
                  </div>
                  {/* Animated connector line flowing into video */}
                  <motion.div initial={{
                  scaleY: 0
                }} whileInView={{
                  scaleY: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.8,
                  delay: 1.6
                }} className="w-px h-12 origin-top" style={{
                  background: 'linear-gradient(to bottom, rgba(217, 119, 6, 0.4), rgba(217, 119, 6, 0.1), transparent)'
                }} data-id="element-1650" />
                  <motion.div animate={{
                  y: [0, 6, 0],
                  opacity: [0.4, 1, 0.4]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1651">
                    <ChevronDown className="w-5 h-5 text-amber-500/60" data-id="element-1652" />
                  </motion.div>
                </div>
              </motion.div>

              {/* Video Player */}
              <div className="max-w-3xl mx-auto mb-8" data-id="element-1653">
                <VideoPlayer src="https://assets.cdn.filesafe.space/sBj5zdNMA4HOTszd0xWs/media/69ca24867aad36d9946f50c1.mp4" data-id="element-1654" />
              </div>

              {/* CTA Button below video */}
              <div className="pt-[20px] pb-[20px] mt-16 sm:mt-20 md:mt-24 mb-20 sm:mb-24 md:mb-28 text-center" data-id="element-1655">
                <button onClick={() => {
                const pricingSection = document.querySelector('[data-pricing-section]');
                if (pricingSection) {
                  pricingSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_60px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 cursor-pointer min-h-[44px]" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1656">
                  YES, I Want to Join the Private Research Order
                </button>
              </div>

              {/* Video hook copy — glassmorphism intelligence panel */}
              <div className="max-w-3xl mx-auto mt-14 sm:mt-18 md:mt-20 relative" style={{
              fontFamily: "'Inter', system-ui, sans-serif"
            }} data-id="element-1657">
                {/* Ambient glow behind panel */}
                <div className="absolute -inset-8 sm:-inset-12 bg-cyan-500/[0.08] rounded-3xl blur-3xl pointer-events-none" data-id="element-1658" />

                {/* Glassmorphism panel */}
                <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 sm:p-8 md:p-10 overflow-hidden" data-id="element-1659">
                  {/* Subtle inner highlight */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-transparent pointer-events-none rounded-xl" data-id="element-1660" />

                  <div className="relative z-10" data-id="element-1661">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100 text-center mb-8 sm:mb-10 leading-snug" style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    textShadow: '0 0 20px rgba(217, 119, 6, 0.15)'
                  }} data-id="element-1662">
                      In this 17-minute clip from an actual{' '}
                      <span className="text-amber-400" data-id="element-1663">PRO</span> livestream,
                      you'll see:
                    </h3>

                    <div className="text-left text-sm sm:text-base md:text-lg text-stone-300 leading-relaxed" data-id="element-1664">
                      <div className="flex items-start gap-3 pb-5 sm:pb-6 border-b border-white/[0.04]" data-id="element-1665">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1666">
                          →
                        </span>
                        <span data-id="element-1667">
                          <span className="text-white font-semibold" data-id="element-1668">
                            Dr. Ali diagnoses a rare genetic disease, in real
                            time
                          </span>
                          , from symptoms a viewer typed into a chat box. No
                          labs. No appointment. Just pattern recognition that
                          left everyone speechless.{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1669">
                            (~0:30)
                          </span>
                        </span>
                      </div>

                      <div className="flex items-start gap-3 py-5 sm:py-6 border-b border-white/[0.04]" data-id="element-1670">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1671">
                          →
                        </span>
                        <span data-id="element-1672">
                          <span className="text-white font-semibold" data-id="element-1673">
                            Brenden Henry showcases results of our own mass
                            spectrometry research
                          </span>{' '}
                          on peptide products, and reveals why we classify
                          bioregulators into Version 1, Version 2 and Version 3.
                          The V1 product? Only 13 peptides. The V3?{' '}
                          <span className="text-amber-400 font-bold" data-id="element-1674">
                            The chart is literally off the scale.
                          </span>{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1675">
                            (~2:20)
                          </span>
                        </span>
                      </div>

                      <div className="flex items-start gap-3 py-5 sm:py-6 border-b border-white/[0.04]" data-id="element-1676">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1677">
                          →
                        </span>
                        <span data-id="element-1678">
                          A complete{' '}
                          <span className="text-white font-semibold" data-id="element-1679">
                            cognitive decline protocol
                          </span>{' '}
                          (exact peptides, exact dosages, exact duration) built
                          from memory in under 90 seconds. For a 70-year-old
                          diabetic. On a live stream.{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1680">
                            (~9:20)
                          </span>
                        </span>
                      </div>

                      <div className="flex items-start gap-3 py-5 sm:py-6 border-b border-white/[0.04]" data-id="element-1681">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1682">
                          →
                        </span>
                        <span data-id="element-1683">
                          A{' '}
                          <span className="text-white font-semibold" data-id="element-1684">
                            paralyzed dog
                          </span>{' '}
                          the vet said to euthanize...{' '}
                          <span className="text-amber-400 font-bold" data-id="element-1685">
                            walking again in TWO WEEKS
                          </span>{' '}
                          on Brenden's peptide protocol. Still walking two years
                          later.{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1686">
                            (~11:15)
                          </span>
                        </span>
                      </div>

                      <div className="flex items-start gap-3 py-5 sm:py-6 border-b border-white/[0.04]" data-id="element-1687">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1688">
                          →
                        </span>
                        <span data-id="element-1689">
                          A member who jumped into the stream to say:{' '}
                          <span className="text-amber-400 font-bold italic" data-id="element-1690">
                            "You healed my thyroid problem."
                          </span>{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1691">
                            (~1:00)
                          </span>
                        </span>
                      </div>

                      <div className="flex items-start gap-3 py-5 sm:py-6 border-b border-white/[0.04]" data-id="element-1692">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1693">
                          →
                        </span>
                        <span data-id="element-1694">
                          The{' '}
                          <span className="text-red-400 font-semibold" data-id="element-1695">
                            "miracle detox"
                          </span>{' '}
                          that's actually destroying your body's master
                          antioxidant, and what you should use instead.{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1696">
                            (~5:00)
                          </span>
                        </span>
                      </div>

                      <div className="flex items-start gap-3 py-5 sm:py-6 border-b border-white/[0.04]" data-id="element-1697">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1698">
                          →
                        </span>
                        <span data-id="element-1699">
                          Why the best peptides have been tested at{' '}
                          <span className="text-white font-semibold" data-id="element-1700">
                            1,000x the normal dose
                          </span>
                          ... with{' '}
                          <span className="text-green-400 font-bold" data-id="element-1701">
                            zero organ damage
                          </span>{' '}
                          and{' '}
                          <span className="text-green-400 font-bold" data-id="element-1702">
                            zero side effects
                          </span>
                          .{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1703">
                            (~12:00)
                          </span>
                        </span>
                      </div>

                      {/* Last bullet — no bottom border */}
                      <div className="flex items-start gap-3 pt-5 sm:pt-6" data-id="element-1704">
                        <span className="text-cyan-400 text-lg sm:text-xl mt-0.5 flex-shrink-0 font-bold" style={{
                        filter: 'drop-shadow(0 0 6px rgba(34, 211, 238, 0.4))'
                      }} data-id="element-1705">
                          →
                        </span>
                        <span data-id="element-1706">
                          Brenden & Dr. Ali building a complete{' '}
                          <span className="text-white font-semibold" data-id="element-1707">
                            osteoporosis reversal protocol. LIVE. From memory.
                          </span>{' '}
                          Finishing each other's sentences. This is what{' '}
                          <span className="text-amber-400 font-bold" data-id="element-1708">
                            "two of the highest-level biomedical minds in the
                            world"
                          </span>{' '}
                          looks like in action.{' '}
                          <span className="text-cyan-400/70 text-sm font-medium" data-id="element-1709">
                            (~12:25)
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Closing paragraph — punchline treatment */}
                    <div className="mt-8 sm:mt-10 bg-amber-500/[0.03] border-l-2 border-amber-500/40 rounded-r-lg p-5 sm:p-6 space-y-4" data-id="element-1710">
                      <p className="text-base sm:text-lg md:text-xl text-stone-300 leading-relaxed" data-id="element-1711">
                        This isn't scripted. These 17 minutes are mostly from a{' '}
                        <span className="text-white font-bold uppercase" data-id="element-1712">
                          single
                        </span>{' '}
                        Q&A session. Normally it costs{' '}
                        <span className="text-amber-400 font-bold" data-id="element-1713">
                          $1,500 an hour
                        </span>{' '}
                        for a consultation with one of them.
                      </p>
                      <p className="text-base sm:text-lg md:text-xl text-stone-100 font-semibold leading-relaxed" data-id="element-1714">
                        But inside the Private Research Order... we've had
                        livestreams where we've gone for{' '}
                        <span className="text-amber-400 font-bold uppercase" style={{
                        textShadow: '0 0 20px rgba(217, 119, 6, 0.3)'
                      }} data-id="element-1715">
                          four hours straight
                        </span>
                        , just answering members' questions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Johnson E. (after video timestamps) */}
              <div className="max-w-3xl mx-auto mt-10 sm:mt-12 relative" data-id="element-1716">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/3 via-transparent to-transparent blur-2xl" data-id="element-1717" />
                <div className="relative px-4 sm:px-8 py-2 sm:py-3" data-id="element-1718">
                  <div className="-mb-4 sm:-mb-6" data-id="element-1719">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1720">
                      "
                    </span>
                  </div>
                  <div className="relative mb-4 sm:mb-5 max-w-3xl mx-auto" data-id="element-1721">
                    <div className="absolute -inset-4 bg-purple-500/5 blur-xl rounded-lg" data-id="element-1722" />
                    <div className="relative border-2 border-purple-500/20 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/10 to-black/40 shadow-[0_0_15px_rgba(168,85,247,0.1),inset_0_0_10px_rgba(168,85,247,0.03)]" data-id="element-1723">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/3 to-transparent pointer-events-none rounded-lg" data-id="element-1724" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1725">
                        <p data-id="element-1726">
                          <span className="text-amber-400 font-semibold" data-id="element-1727">
                            PSI has already had a meaningful impact on how I
                            think
                          </span>{' '}
                          — and I'm only about a month in.
                        </p>
                        <p data-id="element-1728">
                          I believe I was one of the founding memberships when
                          PSI PRO opened, and I can honestly say{' '}
                          <span className="text-white font-bold" data-id="element-1729">
                            I have not regretted the decision for a single
                            second.
                          </span>
                        </p>
                        <p data-id="element-1730">
                          At this stage of my life, my motivation isn't casual
                          curiosity. I want to understand biology deeply. I want
                          to learn properly. And I want to eventually be{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1731">
                            an invaluable resource to others
                          </span>{' '}
                          who are trying to navigate their own health journey —
                          especially when it comes to powerful and emerging
                          protocols like peptides.
                        </p>
                        <p data-id="element-1732">
                          <span className="text-white font-bold" data-id="element-1733">
                            PSI feels like the place where that becomes
                            possible...
                          </span>
                        </p>
                        <p data-id="element-1734">
                          One specific example:{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1735">
                            my understanding of oral peptides completely
                            changed.
                          </span>{' '}
                          I had accepted the social media consensus about
                          bioavailability as fact. But PSI's deeper discussion
                          helped me realize{' '}
                          <span className="text-white font-bold" data-id="element-1736">
                            how incomplete and oversimplified that narrative
                            was.
                          </span>{' '}
                          That was a real "wake up" moment for me.
                        </p>
                        <p data-id="element-1737">
                          I'm also a proud owner of Peptide Mastery, Peptide
                          Salvation, and several of the additional materials.
                          But more than the content itself, what's been most
                          valuable is how{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1738">
                            PSI is training a research mindset.
                          </span>
                        </p>
                        <p className="text-stone-200 font-medium" data-id="element-1739">
                          It's teaching me how to evaluate evidence. How to
                          think clearly. How to separate signal from noise.
                        </p>
                        <p className="text-lg sm:text-xl md:text-2xl" data-id="element-1740">
                          For me, this isn't just education.
                        </p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-400" style={{
                        textShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
                      }} data-id="element-1741">
                          It's clarity.
                        </p>
                        <p className="text-stone-200 font-medium" data-id="element-1742">
                          And I'm grateful to be part of it.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1743">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1744" />
                    <div className="text-center sm:text-right" data-id="element-1745">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-1746">
                        Johnson E.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-1747">
                        Founding PSI PRO Member, Peptide Mastery & Peptide
                        Salvation Owner
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* "Gain powers" transition statement */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="pt-10 sm:pt-14 md:pt-16 pb-6 sm:pb-8 text-center max-w-3xl mx-auto" data-id="element-1748">
                {/* Top decorative line */}
                <div className="flex items-center justify-center gap-4 mb-8 sm:mb-10" data-id="element-1749">
                  <div className="w-16 sm:w-28 h-px" style={{
                  background: 'linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.5))'
                }} data-id="element-1750" />
                  <div className="w-2 h-2 bg-cyan-400/60 rotate-45" data-id="element-1751" />
                  <div className="w-16 sm:w-28 h-px" style={{
                  background: 'linear-gradient(90deg, rgba(34, 211, 238, 0.5), transparent)'
                }} data-id="element-1752" />
                </div>

                <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1753">
                  <span className="block text-stone-400 text-lg sm:text-xl md:text-2xl lg:text-3xl mb-2 sm:mb-3 font-semibold" style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.05)',
                  letterSpacing: '0.02em'
                }} data-id="element-1754">
                    The Private Research Order Will Help You
                  </span>
                  <span className="block text-stone-100" style={{
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.1)'
                }} data-id="element-1755">
                    Gain Powers That Leave Even
                  </span>
                  <span className="block mt-1 sm:mt-2" data-id="element-1756">
                    <span className="text-stone-100" style={{
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.1)'
                  }} data-id="element-1757">
                      The{' '}
                    </span>
                    <span className="relative inline-block" style={{
                    color: '#22d3ee',
                    textShadow: '0 0 30px rgba(34, 211, 238, 0.5), 0 0 60px rgba(34, 211, 238, 0.2)'
                  }} data-id="element-1758">
                      Best Doctors
                    </span>
                    <span className="text-stone-100" style={{
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.1)'
                  }} data-id="element-1759">
                      {' '}
                      In Awe
                    </span>
                  </span>
                </h3>

                {/* Downward pull arrow */}
                <div className="flex justify-center mt-8 sm:mt-10" data-id="element-1760">
                  <motion.div animate={{
                  y: [0, 6, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="flex flex-col items-center gap-1.5" data-id="element-1761">
                    <div className="w-px h-8 bg-gradient-to-b from-transparent via-cyan-400/40 to-cyan-400/60" data-id="element-1762" />
                    <div className="w-2 h-2 bg-cyan-400/60 rotate-45" data-id="element-1763" />
                  </motion.div>
                </div>
              </motion.div>

              {/* What you get — showcasing section with proof testimonials */}
              <div className="max-w-3xl mx-auto mb-0 mt-8 sm:mt-10" data-id="element-1764">
                <div className="space-y-12 sm:space-y-16" data-id="element-1765">
                  {/* Claim 1: Scientific research */}
                  <div data-id="element-1766">
                    <h4 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-center mb-5 sm:mb-6" style={{
                    fontFamily: "'Inter', system-ui, sans-serif"
                  }} data-id="element-1767">
                      <span className="text-stone-100" data-id="element-1768">Gain the </span>
                      <span style={{
                      color: '#22d3ee',
                      textShadow: '0 0 20px rgba(34, 211, 238, 0.3)'
                    }} data-id="element-1769">
                        Latest Scientifically Proven Insights and Methods
                      </span>
                      <span className="text-stone-100" data-id="element-1770">
                        {' '}
                        Straight From Our Ongoing Research
                      </span>
                    </h4>
                    {/* Proof: Ronald R. */}
                    <div className="relative max-w-3xl mx-auto" data-id="element-1771">
                      <div className="absolute -inset-4 bg-purple-500/5 blur-xl rounded-lg" data-id="element-1772" />

                      <div className="relative border-2 border-purple-500/20 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/10 to-black/40 shadow-[0_0_15px_rgba(168,85,247,0.1),inset_0_0_10px_rgba(168,85,247,0.03)]" data-id="element-1773">
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/3 to-transparent pointer-events-none rounded-lg" data-id="element-1774" />

                        <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 font-normal" data-id="element-1775">
                          "I am a{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1776">
                            physician with an integrative medicine practice
                          </span>{' '}
                          and your work has been{' '}
                          <span className="text-white font-bold" data-id="element-1777">
                            very valuable in treating my patients.
                          </span>{' '}
                          When I lecture to physicians I always mention 'peptide
                          mastery' as a great source of material and{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1778">
                            extensive references
                          </span>
                          ..."
                        </p>
                      </div>

                      <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4" data-id="element-1779">
                        <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1780" />
                        <div className="text-left" data-id="element-1781">
                          <p className="text-purple-400 font-semibold text-base sm:text-lg mb-0.5" data-id="element-1782">
                            Ronald R.
                          </p>
                          <p className="text-stone-500 text-sm font-normal" data-id="element-1783">
                            Physician & Peptide Mastery Owner
                          </p>
                        </div>
                        <div className="h-px flex-1 bg-gradient-to-l from-purple-500/40 to-transparent" data-id="element-1784" />
                      </div>
                    </div>
                  </div>

                  {/* Claim 2: Health transformations */}
                  <div data-id="element-1785">
                    <h4 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-center mb-5 sm:mb-6" style={{
                    fontFamily: "'Inter', system-ui, sans-serif"
                  }} data-id="element-1786">
                      <span className="text-stone-100" data-id="element-1787">Experience </span>
                      <span style={{
                      color: '#22d3ee',
                      textShadow: '0 0 20px rgba(34, 211, 238, 0.3)'
                    }} data-id="element-1788">
                        Real, Measurable Health Transformations
                      </span>
                      <span className="text-stone-100" data-id="element-1789">
                        {' '}
                        Even for Conditions the Mainstream Says Are Untreatable
                      </span>
                    </h4>
                    {/* Proof: Sean L. */}
                    <div className="relative max-w-3xl mx-auto" data-id="element-1790">
                      <div className="absolute -inset-4 bg-purple-500/5 blur-xl rounded-lg" data-id="element-1791" />

                      <div className="relative border-2 border-purple-500/20 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/10 to-black/40 shadow-[0_0_15px_rgba(168,85,247,0.1),inset_0_0_10px_rgba(168,85,247,0.03)]" data-id="element-1792">
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/3 to-transparent pointer-events-none rounded-lg" data-id="element-1793" />

                        <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 font-normal" data-id="element-1794">
                          "Working with this team is{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1795">
                            guaranteed to provide transformations
                          </span>
                          … They're doing something{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1796">
                            revolutionary
                          </span>{' '}
                          here. If you want to{' '}
                          <span className="text-white font-bold" data-id="element-1797">
                            attack diseases that the mainstream says is not
                            really something treatable
                          </span>
                          ...{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1798">
                            it will blow your mind.
                          </span>
                          "
                        </p>
                      </div>

                      <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4" data-id="element-1799">
                        <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1800" />
                        <div className="flex items-center gap-3 sm:gap-4" data-id="element-1801">
                          <div className="relative flex-shrink-0" data-id="element-1802">
                            <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-1803" />
                            <img src="https://cdn.magicpatterns.com/uploads/8TaJVJxgg4BMR3q5DqSNsr/SeanL.jpg" alt="Sean L." className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-3 border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.3)]" data-id="element-1804" />
                          </div>
                          <div className="text-left" data-id="element-1805">
                            <p className="text-purple-400 font-semibold text-base sm:text-lg mb-0.5" data-id="element-1806">
                              Sean L.
                            </p>
                            <p className="text-stone-500 text-sm font-normal" data-id="element-1807">
                              Transformation Services Client
                            </p>
                          </div>
                        </div>
                        <div className="h-px flex-1 bg-gradient-to-l from-purple-500/40 to-transparent" data-id="element-1808" />
                      </div>
                    </div>
                  </div>

                  {/* Claim 3: Separate fact from fiction */}
                  <div data-id="element-1809">
                    <h4 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-center mb-5 sm:mb-6" style={{
                    fontFamily: "'Inter', system-ui, sans-serif"
                  }} data-id="element-1810">
                      <span className="text-stone-100" data-id="element-1811">Finally </span>
                      <span className="text-white" style={{
                      textShadow: '0 0 20px rgba(255, 255, 255, 0.15)'
                    }} data-id="element-1812">
                        Separate Fact From Fiction
                      </span>
                      <span className="text-stone-100" data-id="element-1813">
                        {' '}
                        So You Stop Wasting Time on Protocols That Don't Work
                      </span>
                    </h4>
                    {/* Proof: Max O. */}
                    <div className="relative max-w-3xl mx-auto" data-id="element-1814">
                      <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1815" />

                      <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1816">
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1817" />

                        <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 font-normal" data-id="element-1818">
                          "The{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1819">
                            exceptional quality of your content
                          </span>{' '}
                          is{' '}
                          <span className="text-white font-bold" data-id="element-1820">
                            way better (and more accurate) than what can be
                            found nowadays on the internet
                          </span>{' '}
                          (and it's not even close)...{' '}
                          <span className="text-white font-bold" data-id="element-1821">
                            Your work is truly making a difference.
                          </span>
                          "
                        </p>
                      </div>

                      <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4" data-id="element-1822">
                        <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1823" />
                        <div className="text-left" data-id="element-1824">
                          <p className="text-purple-400 font-semibold text-base sm:text-lg mb-0.5" data-id="element-1825">
                            Max O.
                          </p>
                          <p className="text-stone-500 text-sm font-normal" data-id="element-1826">
                            Peptide/Biohacking Store Owner (10+ years)
                          </p>
                        </div>
                        <div className="h-px flex-1 bg-gradient-to-l from-purple-500/40 to-transparent" data-id="element-1827" />
                      </div>
                    </div>
                  </div>

                  {/* Claim 4: Access to experts */}
                  <div data-id="element-1828">
                    <h4 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-center mb-5 sm:mb-6" style={{
                    fontFamily: "'Inter', system-ui, sans-serif"
                  }} data-id="element-1829">
                      <span className="text-stone-100" data-id="element-1830">
                        Get Direct, Personalized Answers From{' '}
                      </span>
                      <span style={{
                      color: '#22d3ee',
                      textShadow: '0 0 20px rgba(34, 211, 238, 0.3)'
                    }} data-id="element-1831">
                        Two of the Highest-Level Biomedical Minds in the World
                      </span>
                      <span className="text-stone-100" data-id="element-1832"> on </span>
                      <span className="text-white underline decoration-cyan-400/50 underline-offset-4" style={{
                      textShadow: '0 0 15px rgba(255, 255, 255, 0.1)'
                    }} data-id="element-1833">
                        Almost Any Question You Have
                      </span>
                    </h4>
                    {/* Proof: Elizabeth N. */}
                    <div className="relative max-w-3xl mx-auto" data-id="element-1834">
                      <div className="absolute -inset-4 bg-purple-500/5 blur-xl rounded-lg" data-id="element-1835" />

                      <div className="relative border-2 border-purple-500/20 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/10 to-black/40 shadow-[0_0_15px_rgba(168,85,247,0.1),inset_0_0_10px_rgba(168,85,247,0.03)]" data-id="element-1836">
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/3 to-transparent pointer-events-none rounded-lg" data-id="element-1837" />

                        <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 font-normal" data-id="element-1838">
                          "He's able to predict outcomes and potential
                          applications that{' '}
                          <span className="text-stone-100 font-semibold" data-id="element-1839">
                            others simply don't see.
                          </span>{' '}
                          As the director of ReadyMed, I've worked with
                          countless physicians, hormone specialists, and
                          pharmacists over the years. But I can say without
                          hesitation that Brenden Henry's knowledge of peptides,
                          pharmacology, and human biology is{' '}
                          <span className="text-amber-400 font-bold" data-id="element-1840">
                            truly in a league of its own.
                          </span>
                          "
                        </p>
                      </div>

                      <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4" data-id="element-1841">
                        <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1842" />
                        <div className="flex items-center gap-3 sm:gap-4" data-id="element-1843">
                          <div className="relative flex-shrink-0" data-id="element-1844">
                            <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-1845" />
                            <img src="https://cdn.magicpatterns.com/uploads/7z7kG6cfruhkmzvLk7oX76/image09.jpg" alt="Elizabeth N." className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-3 border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.3)]" data-id="element-1846" />
                          </div>
                          <div className="text-left" data-id="element-1847">
                            <p className="text-purple-400 font-semibold text-base sm:text-lg mb-0.5" data-id="element-1848">
                              Elizabeth N.
                            </p>
                            <p className="text-stone-500 text-sm font-normal" data-id="element-1849">
                              Director at ReadyMed
                            </p>
                          </div>
                        </div>
                        <div className="h-px flex-1 bg-gradient-to-l from-purple-500/40 to-transparent" data-id="element-1850" />
                      </div>
                    </div>

                    {/* Proof: Andrew W. — about Dr. Ali */}
                    <div className="relative max-w-3xl mx-auto mt-6" data-id="element-1851">
                      <div className="absolute -inset-4 bg-purple-500/5 blur-xl rounded-lg" data-id="element-1852" />

                      <div className="relative border-2 border-purple-500/20 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/10 to-black/40 shadow-[0_0_15px_rgba(168,85,247,0.1),inset_0_0_10px_rgba(168,85,247,0.03)]" data-id="element-1853">
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/3 to-transparent pointer-events-none rounded-lg" data-id="element-1854" />

                        <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 font-normal" data-id="element-1855">
                          "
                          <span className="text-white font-semibold" data-id="element-1856">
                            Dr Ali's mind is like a supercomputer
                          </span>
                          "
                        </p>
                      </div>

                      <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4" data-id="element-1857">
                        <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1858" />
                        <div className="text-left" data-id="element-1859">
                          <p className="text-purple-400 font-semibold text-base sm:text-lg mb-0.5" data-id="element-1860">
                            Andrew W.
                          </p>
                          <p className="text-stone-500 text-sm font-normal" data-id="element-1861">
                            Founding PRO Member & Peptide Salvation Owner
                          </p>
                        </div>
                        <div className="h-px flex-1 bg-gradient-to-l from-purple-500/40 to-transparent" data-id="element-1862" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Button below Andrew W. testimonial */}
              <div style={{
              marginTop: '60px',
              marginBottom: '60px'
            }} className="sm:!mt-[80px] sm:!mb-[80px] md:!mt-[100px] md:!mb-[100px] text-center" data-id="element-1863">
                <button onClick={() => {
                const pricingSection = document.querySelector('[data-pricing-section]');
                if (pricingSection) {
                  pricingSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_60px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 cursor-pointer min-h-[44px]" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1864">
                  YES, I Want to Join the Private Research Order
                </button>
              </div>

              {/* Three punchy lines with dividers — stone/amber tones */}
              <div className="max-w-2xl mx-auto space-y-0" data-id="element-1865"></div>

              {/* Closing line — elevated as a standalone beat */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="mt-28 sm:mt-36 md:mt-44 mb-6 sm:mb-8 text-center relative" data-id="element-1866">
                {/* Decorative divider above */}
                <div className="flex items-center justify-center gap-4 mb-10 sm:mb-14" data-id="element-1867">
                  <div className="w-20 sm:w-32 h-px" style={{
                  background: 'linear-gradient(90deg, transparent, #d97706, #fbbf24)'
                }} data-id="element-1868" />
                  <div className="w-2.5 h-2.5 bg-amber-500/70 rotate-45" data-id="element-1869" />
                  <div className="w-20 sm:w-32 h-px" style={{
                  background: 'linear-gradient(90deg, #fbbf24, #d97706, transparent)'
                }} data-id="element-1870" />
                </div>

                {/* Ambient glow */}
                <div className="absolute inset-0 bg-gradient-radial from-amber-600/[0.06] via-transparent to-transparent blur-3xl pointer-events-none" data-id="element-1871" />

                <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 relative z-10" style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                textShadow: '0 0 40px rgba(217, 119, 6, 0.5), 0 0 80px rgba(217, 119, 6, 0.2)'
              }} data-id="element-1872">
                  This is where the future of human biology is being written —
                </p>
                <p className="mt-4 sm:mt-5 text-3xl sm:text-4xl md:text-5xl font-bold text-amber-400 tracking-wide relative z-10" style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                textShadow: '0 0 40px rgba(217, 119, 6, 0.5), 0 0 80px rgba(217, 119, 6, 0.2)'
              }} data-id="element-1873">
                  In Private.
                </p>

                {/* Decorative divider below */}
              </motion.div>

              {/* Enrollment line */}
            </div>
          </motion.div>

          {/* === TONAL SHIFT — amber fades to red === */}
          <div className="max-w-3xl mx-auto mt-10 sm:mt-14 mb-2" data-id="element-1874">
            {/* Gradient divider: amber → red transition */}
            <div className="flex items-center justify-center gap-3 sm:gap-4" data-id="element-1875">
              <div className="w-16 sm:w-28 h-px" style={{
              background: 'linear-gradient(90deg, transparent, #d97706, #ef4444)'
            }} data-id="element-1876" />
              <div className="w-2 h-2 bg-red-500/60 rotate-45" data-id="element-1877" />
              <div className="w-16 sm:w-28 h-px" style={{
              background: 'linear-gradient(90deg, #ef4444, #d97706, transparent)'
            }} data-id="element-1878" />
            </div>
          </div>

          {/* BS callout — hard-hitting line */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mt-4 sm:mt-6 max-w-3xl mx-auto" data-id="element-1879">
            {/* BUT BE WARNED — dramatic attention hook */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7,
            type: 'spring',
            stiffness: 120
          }} className="text-center mb-6 sm:mb-8" data-id="element-1880">
              {/* Top decorative line */}
              <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-5" data-id="element-1881">
                <div className="w-12 sm:w-20 h-px bg-gradient-to-r from-transparent to-red-500/60" data-id="element-1882" />
                <div className="w-1.5 h-1.5 bg-red-500/70 rotate-45" data-id="element-1883" />
                <div className="w-12 sm:w-20 h-px bg-gradient-to-l from-transparent to-red-500/60" data-id="element-1884" />
              </div>

              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-wider uppercase" style={{
              fontFamily: 'Cinzel, serif',
              color: '#ef4444',
              textShadow: '0 0 60px rgba(239, 68, 68, 0.6), 0 0 120px rgba(239, 68, 68, 0.3), 0 4px 8px rgba(0,0,0,0.8)',
              letterSpacing: '0.12em'
            }} data-id="element-1885">
                But Be Warned
              </h2>

              {/* Bottom decorative line */}
              <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4 sm:mt-5" data-id="element-1886">
                <div className="w-12 sm:w-20 h-px bg-gradient-to-r from-transparent to-red-500/60" data-id="element-1887" />
                <div className="w-1.5 h-1.5 bg-red-500/70 rotate-45" data-id="element-1888" />
                <div className="w-12 sm:w-20 h-px bg-gradient-to-l from-transparent to-red-500/60" data-id="element-1889" />
              </div>
            </motion.div>

            {/* Downward arrow pointing at callout */}
            <div className="flex justify-center mb-3" data-id="element-1890">
              <div className="flex flex-col items-center gap-0.5" data-id="element-1891">
                <div className="w-px h-5 bg-gradient-to-b from-transparent to-red-500/60" data-id="element-1892" />
                <svg className="w-4 h-4 text-red-500/70" viewBox="0 0 16 16" fill="currentColor" data-id="element-1893">
                  <path d="M8 12L2 6h12L8 12z" data-id="element-1894" />
                </svg>
              </div>
            </div>
            <div className="px-6 sm:px-8 py-4 sm:py-5 border-l-4 border-r-4 border-red-500/60 bg-red-950/15 rounded-lg" data-id="element-1895">
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-stone-200 text-center leading-snug" style={{
              textShadow: '0 0 15px rgba(239, 68, 68, 0.15)'
            }} data-id="element-1896">
                In PUBLIC the amount of unproven, harmful, false and lazy
                information about peptides out there is{' '}
                <span className="text-red-400 font-extrabold uppercase tracking-wide" data-id="element-1897">
                  MIND BOGGLING
                </span>
              </p>
            </div>
          </motion.div>

          {/* DID YOU KNOW — BPC-157 vs mystery peptide */}
          <motion.div initial={{
          opacity: 0,
          y: 25
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.7
        }} className="mt-8 sm:mt-10 max-w-4xl mx-auto" data-id="element-1898">
            <div className="p-6 sm:p-8 md:p-10 bg-gradient-to-br from-stone-950/60 via-black/80 to-stone-950/40 border border-amber-900/30 rounded-xl" data-id="element-1899">
              {/* Header */}
              {/* Headline — bold claim */}
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 text-center mb-5 sm:mb-6" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 25px rgba(217, 119, 6, 0.3)'
            }} data-id="element-1900">
                Almost Everything You See in Public Is Wrong.
              </h3>

              {/* Earl Nightingale quote — borrowed authority with portrait */}
              <blockquote className="relative max-w-2xl mx-auto mb-6 sm:mb-8 px-5 sm:px-6 py-5 sm:py-6 border-l-2 border-amber-600/40 bg-amber-950/10 rounded-r-lg" data-id="element-1901">
                <p className="text-base sm:text-lg md:text-xl text-stone-300 italic leading-relaxed" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1902">
                  "If you want to be successful in life, simply watch what most
                  people would do in a given situation, and then do the total
                  opposite...{' '}
                  <span className="text-amber-400 font-semibold not-italic" data-id="element-1903">
                    The majority is always wrong.
                  </span>
                  "
                </p>
                <footer className="mt-5 flex flex-col items-center gap-3" data-id="element-1904">
                  <div className="relative flex-shrink-0" data-id="element-1905">
                    <div className="absolute inset-0 bg-amber-500/15 blur-lg rounded-full" data-id="element-1906" />
                    <img src="https://miro.medium.com/1*1fkcweahaTZWhrsa1OHPgQ.jpeg" alt="Earl Nightingale" className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-amber-600/40 grayscale" onError={e => {
                    ;
                    (e.target as HTMLImageElement).style.display = 'none';
                  }} data-id="element-1907" />
                  </div>
                  <div className="text-center" data-id="element-1908">
                    <p className="text-sm sm:text-base text-amber-500/80 font-semibold" data-id="element-1909">
                      Earl Nightingale
                    </p>
                    <p className="text-xs sm:text-sm text-stone-600 font-normal" data-id="element-1910">
                      Author & Speaker — "Dean of Personal Development"
                    </p>
                  </div>
                </footer>
              </blockquote>

              <div className="space-y-5 sm:space-y-6 text-sm sm:text-base md:text-lg text-stone-300 leading-relaxed max-w-3xl mx-auto" data-id="element-1911">
                {/* Transition line into BPC-157 example */}
                <p className="text-base sm:text-lg md:text-xl text-stone-400 text-center italic pb-2" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1912">
                  I wrote a{' '}
                  <span className="text-stone-200 font-semibold not-italic" data-id="element-1913">
                    LOT
                  </span>{' '}
                  of examples of the "majority" always being wrong further down
                  this page. But let's start with{' '}
                  <span className="text-amber-400 font-semibold not-italic" data-id="element-1914">
                    one of the biggest
                  </span>
                  .
                </p>

                {/* BPC-157 intro */}
                <p data-id="element-1915">
                  The entire biohacking world is obsessed with{' '}
                  <span className="text-stone-100 font-bold" data-id="element-1916">BPC-157</span>.
                  It's practically worshipped as the{' '}
                  <span className="text-stone-100 italic" data-id="element-1917">
                    "ultimate healing peptide."
                  </span>
                </p>

                {/* Risks */}
                <p data-id="element-1918">
                  But most people using it have no idea that BPC-157 carries a
                  potential risk of{' '}
                  <span className="text-red-400 font-semibold" data-id="element-1919">anhedonia</span>{' '}
                  — a disturbing emotional blunting where you lose the ability
                  to feel pleasure or motivation. They also don't know it may{' '}
                  <span className="text-red-400 font-semibold" data-id="element-1920">
                    accelerate cancerous tumor growth
                  </span>
                  , making it a legitimate risk for people with underlying (and
                  sometimes hidden) cancer.
                </p>

                {/* Mystery peptide intro */}
                <p data-id="element-1921">
                  Meanwhile, there is a{' '}
                  <span className="text-amber-400 font-bold" data-id="element-1922">
                    tiny 2-amino-acid peptide
                  </span>{' '}
                  that most biohackers have never even heard of that operates on
                  a completely different level.
                </p>

                {/* Superior claim */}
                <p className="text-base sm:text-lg md:text-xl text-stone-100 font-bold text-center py-2" data-id="element-1923">
                  It's been{' '}
                  <span className="text-green-400 uppercase tracking-wide" data-id="element-1924">
                    PROVEN
                  </span>{' '}
                  to be superior to BPC-157 in almost every way:
                </p>

                {/* Bullet points */}
                <ul className="space-y-4" data-id="element-1925">
                  <li className="flex items-start gap-3" data-id="element-1926">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1927">
                      →
                    </span>
                    <span data-id="element-1928">
                      It can{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1929">
                        enter the cell nucleus
                      </span>{' '}
                      and physically reactivate youthful genes inside your very
                      DNA — even ones that have been silenced for years.{' '}
                      <span className="text-stone-500 italic" data-id="element-1930">
                        BPC-157 has never been proven to do this.
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1931">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1932">
                      →
                    </span>
                    <span data-id="element-1933">
                      It simultaneously activates both{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1934">
                        massive tissue regeneration (IGF-1)
                      </span>{' '}
                      AND{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1935">
                        cellular longevity pathways (Sirtuins 1 & 6)
                      </span>{' '}
                      — two systems that scientists believed were{' '}
                      <span className="text-stone-100 font-bold italic" data-id="element-1936">
                        biologically impossible to trigger at the same time!
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1937">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1938">
                      →
                    </span>
                    <span data-id="element-1939">
                      It has been proven to{' '}
                      <span className="text-green-400 font-semibold" data-id="element-1940">
                        repair crushed limbs
                      </span>{' '}
                      containing dead, necrotic tissue, where it literally{' '}
                      <span className="text-stone-100 font-bold" data-id="element-1941">
                        regenerated 75% of surgically removed liver tissue
                      </span>
                      .
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1942">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1943">
                      →
                    </span>
                    <span data-id="element-1944">
                      It has{' '}
                      <span className="text-green-400 font-bold" data-id="element-1945">
                        zero reported side effects
                      </span>{' '}
                      — even at{' '}
                      <span className="text-stone-100 font-semibold" data-id="element-1946">
                        1,000 times
                      </span>{' '}
                      the normal therapeutic dose!
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1947">
                    <span className="text-amber-500 mt-1 flex-shrink-0 font-bold" data-id="element-1948">
                      →
                    </span>
                    <span data-id="element-1949">
                      And it does{' '}
                      <span className="text-green-400 font-bold uppercase" data-id="element-1950">
                        NOT
                      </span>{' '}
                      require injection.
                    </span>
                  </li>
                </ul>

                {/* Closing tie-back */}
                <div className="pt-4 mt-4 border-t border-amber-900/30 text-center" data-id="element-1951">
                  <p className="text-base sm:text-lg text-stone-400" data-id="element-1952">
                    This is just{' '}
                    <span className="text-amber-400 font-bold" data-id="element-1953">
                      ONE peptide
                    </span>{' '}
                    out of the{' '}
                    <span className="text-stone-200 font-semibold" data-id="element-1954">
                      HUNDREDS
                    </span>{' '}
                    of other peptides, bioregulators, bioaugmentors and
                    compounds that we cover inside the{' '}
                    <span className="text-purple-300 font-semibold" data-id="element-1955">
                      Private Research Order
                    </span>{' '}
                    and{' '}
                    <span className="text-green-400 font-semibold" data-id="element-1956">
                      Peptide Mastery
                    </span>
                    .
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* TESTIMONIALS — Jeff S., Michael E., Johnson E. */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 1.55
        }} className="mt-12 sm:mt-16 mb-10 sm:mb-16 max-w-4xl mx-auto" data-id="element-1957">
            {/* Section intro */}
            <div className="text-center mb-10 sm:mb-14" data-id="element-1958">
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-1959">
                <div className="w-12 sm:w-20 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-1960" />
                <div className="w-2.5 h-2.5 bg-amber-500 rotate-45" data-id="element-1961" />
                <div className="w-12 sm:w-20 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-1962" />
              </div>
              <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4" data-id="element-1963">
                <p className="text-xl sm:text-2xl md:text-3xl text-stone-400 font-medium tracking-wide" style={{
                fontFamily: 'Cinzel, serif'
              }} data-id="element-1964">
                  The Transformations.
                </p>
                <p className="text-xl sm:text-2xl md:text-3xl text-stone-400 font-medium tracking-wide" style={{
                fontFamily: 'Cinzel, serif'
              }} data-id="element-1965">
                  The Insights.
                </p>
                <p className="text-xl sm:text-2xl md:text-3xl text-stone-400 font-medium tracking-wide" style={{
                fontFamily: 'Cinzel, serif'
              }} data-id="element-1966">
                  The Revelations.
                </p>

                <div className="pt-2 sm:pt-4" data-id="element-1967">
                  <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-none" style={{
                  fontFamily: 'Cinzel, serif'
                }} data-id="element-1968">
                    <span className="text-amber-400" style={{
                    textShadow: '0 0 50px rgba(217, 119, 6, 0.6), 0 0 100px rgba(217, 119, 6, 0.3)'
                  }} data-id="element-1969">
                      THEY'RE ALL REAL
                    </span>
                  </p>
                </div>

                <div className="pt-2 sm:pt-3" data-id="element-1970">
                  <p className="text-lg sm:text-xl md:text-2xl text-stone-300 font-semibold leading-relaxed" style={{
                  fontFamily: 'Cinzel, serif',
                  textShadow: '0 0 15px rgba(217, 119, 6, 0.15)'
                }} data-id="element-1971">
                    And they're happening{' '}
                    <span className="text-amber-300 font-bold italic" data-id="element-1972">
                      right now
                    </span>
                  </p>
                  <p className="text-lg sm:text-xl md:text-2xl text-stone-300 font-semibold leading-relaxed mt-1" style={{
                  fontFamily: 'Cinzel, serif',
                  textShadow: '0 0 15px rgba(147, 51, 234, 0.2)'
                }} data-id="element-1973">
                    inside the{' '}
                    <span className="text-purple-300 font-bold" style={{
                    textShadow: '0 0 25px rgba(147, 51, 234, 0.4)'
                  }} data-id="element-1974">
                      Private Research Order
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-12 sm:space-y-16" data-id="element-1975">
              {/* Testimonial — Jeff S. */}
              <div className="relative" data-id="element-1976">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-1977" />
                <div className="relative px-4 sm:px-8 py-2 sm:py-3" data-id="element-1978">
                  <div className="-mb-4 sm:-mb-6" data-id="element-1979">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-1980">
                      "
                    </span>
                  </div>
                  <div className="relative mb-4 sm:mb-5 max-w-3xl mx-auto" data-id="element-1981">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-1982" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-1983">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-1984" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-1985">
                        <p data-id="element-1986">
                          Hi Brenden, Dr Ali, Dan and team, First off, I wanted
                          to congratulate you for the work that you do at PSI.{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1987">
                            It is unique, inspiring and brings the tremendous
                            potential of bringing Professor Khavinson's work
                            into the sunlight
                          </span>{' '}
                          after many years out of sight. I only discovered PSI
                          about a month ago despite many years of working in
                          peptide science / regenerative medicine research work
                          and{' '}
                          <span className="text-white font-bold" data-id="element-1988">
                            it has thrown me for a loop...
                          </span>
                        </p>
                        <p data-id="element-1989">
                          Brenden, I speak a tiny bit of Russian and{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1990">
                            my hat is off to you for jumping in to translate the
                            700+ Khavinson articles
                          </span>{' '}
                          and the many other Russian journals that covered his
                          work.{' '}
                          <span className="text-white font-bold" data-id="element-1991">
                            You clearly have a brilliant mind and the ability to
                            focus
                          </span>
                          , not to mention a great tolerance for the pain you
                          inflicted upon yourself from the difficulty and volume
                          of effort this must have entailed.
                        </p>
                        <p data-id="element-1992">
                          As I'm sure you know,{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1993">
                            the US docs I work with don't talk about Khavinson's
                            work
                          </span>{' '}
                          (if they even know of it)...
                        </p>
                        <p data-id="element-1994">
                          I'm now doing my best to catch up to the recent live
                          calls with Brenden and Dr Ali (as moderated by Dan) as
                          well as go through the libraries / courses of Peptide
                          Mastery and whatever else I can find.
                        </p>
                        <p data-id="element-1995">
                          So again, I wanted to offer a note of appreciation.{' '}
                          <span className="text-white font-bold" data-id="element-1996">
                            Even the commonly known research peptides are not
                            even mentioned in the Mayo regenerative medicine
                            research we are doing
                          </span>
                          , much less the{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-1997">
                            Khavinson peptides that only you gentlemen have a
                            grasp on.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1998">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-1999" />
                    <div className="text-center sm:text-right" data-id="element-2000">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2001">
                        Jeff S.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2002">
                        Peptide Science / Regenerative Medicine Researcher
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Elizabeth N. */}
              <div className="relative" data-id="element-2003">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2004" />
                <div className="relative px-4 sm:px-8 py-2 sm:py-3" data-id="element-2005">
                  <div className="-mb-4 sm:-mb-6" data-id="element-2006">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2007">
                      "
                    </span>
                  </div>
                  <div className="relative mb-4 sm:mb-5 max-w-3xl mx-auto" data-id="element-2008">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2009" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2010">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2011" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-2012">
                        <p data-id="element-2013">
                          I've seen Brenden dissect complex pharmacological
                          mechanisms and explain them in a way that makes sense
                          to both medical professionals and laypeople alike.
                        </p>
                        <p data-id="element-2014">
                          He's able to{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2015">
                            predict outcomes and potential applications that
                            others simply don't see.
                          </span>
                        </p>
                        <p data-id="element-2016">
                          He's not afraid to challenge conventional wisdom when
                          the science doesn't support it. In a field often
                          clouded by hype and misinformation,{' '}
                          <span className="text-white font-bold" data-id="element-2017">
                            Brenden's dedication to truth is refreshing and
                            invaluable.
                          </span>
                        </p>
                        <p data-id="element-2018">
                          As the director of ReadyMed, I've worked with
                          countless physicians, hormone specialists, and
                          pharmacists over the years. But I can say without
                          hesitation that{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2019">
                            Brenden Henry's knowledge of peptides, pharmacology,
                            and human biology is truly in a league of its own.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2020">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2021" />
                    <div className="flex items-center gap-3 sm:gap-4" data-id="element-2022">
                      <div className="relative flex-shrink-0" data-id="element-2023">
                        <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2024" />
                        <img src="https://cdn.magicpatterns.com/uploads/7z7kG6cfruhkmzvLk7oX76/image09.jpg" alt="Elizabeth N." className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-3 border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.3)]" data-id="element-2025" />
                      </div>
                      <div className="text-left" data-id="element-2026">
                        <p className="text-purple-400 font-semibold text-base sm:text-lg mb-0.5" data-id="element-2027">
                          Elizabeth N.
                        </p>
                        <p className="text-stone-500 text-sm font-normal" data-id="element-2028">
                          Director at ReadyMed, Peptide Mastery buyer
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Johnson E. */}
              <div className="relative" data-id="element-2029">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2030" />
              </div>

              {/* Testimonial — Moussa S. */}
              <div className="relative" data-id="element-2031">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2032" />
                <div className="relative px-4 sm:px-8 py-2 sm:py-3" data-id="element-2033">
                  <div className="-mb-4 sm:-mb-6" data-id="element-2034">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2035">
                      "
                    </span>
                  </div>
                  <div className="relative mb-4 sm:mb-5 max-w-3xl mx-auto" data-id="element-2036">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2037" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2038">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2039" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-2040">
                        <p data-id="element-2041">Hello Dan and the Whole team,</p>
                        <p data-id="element-2042">
                          A couple days ago, I completed my first reading of the
                          Peptide Mastery Complete course. All I can say is{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2043">
                            "Bravo!"
                          </span>{' '}
                          It is so interesting,{' '}
                          <span className="text-white font-bold" data-id="element-2044">
                            I could not stop myself going through it...every
                            night, some nights until 3:00 AM.
                          </span>
                        </p>
                        <p data-id="element-2045">
                          <span className="text-amber-400 font-semibold" data-id="element-2046">
                            Thank you x10!
                          </span>{' '}
                          for creating and making accessible{' '}
                          <span className="text-white font-bold" data-id="element-2047">
                            such a deep knowledge regarding human health.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2048">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2049" />
                    <div className="text-center sm:text-right" data-id="element-2050">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2051">
                        Moussa S.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2052">
                        Founding PSI PRO Member & Peptide Mastery Owner
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Shawna C. */}
              <div className="relative" data-id="element-2053">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2054" />
                <div className="relative px-4 sm:px-8 py-2 sm:py-3" data-id="element-2055">
                  <div className="-mb-4 sm:-mb-6" data-id="element-2056">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2057">
                      "
                    </span>
                  </div>
                  <div className="relative mb-4 sm:mb-5 max-w-3xl mx-auto" data-id="element-2058">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2059" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2060">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2061" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-2062">
                        <p data-id="element-2063">
                          At a most challenging time in my life{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2064">
                            you are all truly a gift from the Universe.
                          </span>{' '}
                          I count my blessings daily!
                        </p>
                        <p data-id="element-2065">
                          Ali, You and Brenden —{' '}
                          <span className="text-white font-bold" data-id="element-2066">
                            the best triple threat in the Healthspan/Lifespan
                            space!
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2067">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2068" />
                    <div className="text-center sm:text-right" data-id="element-2069">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2070">
                        Shawna C.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2071">
                        Transformation Client & Peptide Mastery Owner
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonial — Max O. */}
              <div className="relative" data-id="element-2072">
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2073" />
                <div className="relative px-4 sm:px-8 py-2 sm:py-3" data-id="element-2074">
                  <div className="-mb-4 sm:-mb-6" data-id="element-2075">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2076">
                      "
                    </span>
                  </div>
                  <div className="relative mb-4 sm:mb-5 max-w-3xl mx-auto" data-id="element-2077">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2078" />
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2079">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2080" />
                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-2081">
                        <p data-id="element-2082">
                          I'm writing first to express my sincere appreciation
                          for the{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2083">
                            exceptional quality of your content
                          </span>{' '}
                          which is{' '}
                          <span className="text-white font-bold" data-id="element-2084">
                            way better (and more accurate) than what can be
                            found nowadays on the internet
                          </span>{' '}
                          (and it's not even close)...
                        </p>
                        <p data-id="element-2085">
                          Please know that{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2086">
                            I greatly admire the contribution you and your team
                            are making
                          </span>{' '}
                          to the popularization and understanding of peptides.{' '}
                          <span className="text-white font-bold" data-id="element-2087">
                            Your work is truly making a difference.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2088">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2089" />
                    <div className="text-center sm:text-right" data-id="element-2090">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2091">
                        Max O.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2092">
                        Peptide Mastery Owner & Peptide/Biohacking Store Owner
                        (10+ years)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Trust bar — social proof */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="mt-10 sm:mt-12 flex justify-center max-w-4xl mx-auto" data-id="element-2093"></motion.div>

          {/* Gift transition — Peptide Mastery included free */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.85
        }} className="mt-10 sm:mt-12 max-w-3xl mx-auto text-center" data-free-scholarship-section data-id="element-2094">
            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mb-6" data-id="element-2095">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-amber-600/50" data-id="element-2096" />
              <span className="text-amber-500 text-lg" data-id="element-2097">✦</span>
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-amber-600/50" data-id="element-2098" />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-stone-400 leading-relaxed mb-4" data-id="element-2099">
              And as if that weren't enough...
            </p>

            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-200 leading-snug mb-4" style={{
            textShadow: '0 0 15px rgba(34, 197, 94, 0.15)'
          }} data-id="element-2100">
              Every member also receives a{' '}
              <span className="text-green-400 font-extrabold" data-id="element-2101">FREE</span>{' '}
              scholarship to the most{' '}
              <span className="text-amber-400 font-bold" data-id="element-2102">
                elite, comprehensive, in-depth peptide education
              </span>{' '}
              ever created
            </p>

            <p className="sm:text-base italic text-[20px] text-[#D3A482]" data-id="element-2103">
              Just for being a member. No extra cost. No catch.
            </p>
          </motion.div>
        </motion.div>

        {/* Sub-headline - Completely redesigned with scholarship and access messaging */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 1.8
      }} className="mb-8 sm:mb-12 relative" data-id="element-2104">
          {/* Dramatic spotlight effect - reduced */}
          <div className="absolute inset-0 bg-gradient-radial from-green-600/5 via-transparent to-transparent blur-3xl" data-id="element-2105" />

          <div className="relative max-w-5xl mx-auto" data-id="element-2106">
            {/* Scholarship announcement box */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 2.0
          }} className="p-6 sm:p-8 bg-gradient-to-br from-green-950/30 to-black/40 border-2 border-green-500/40 rounded-xl shadow-[0_0_40px_rgba(34,197,94,0.2)] mb-6" data-id="element-2107">
              <div className="text-center space-y-4 sm:space-y-5" data-id="element-2108">
                {/* Lead with the magnitude — what it IS before what it's called */}
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-300 leading-tight" style={{
                textShadow: '0 0 20px rgba(34, 197, 94, 0.3)'
              }} data-id="element-2109">
                  Including the Most Comprehensive, In-Depth Peptide Education
                  Ever Created — Yours FREE
                </p>

                {/* The impressive stats that prove the claim */}
                <div className="text-sm sm:text-base text-stone-300 leading-relaxed max-w-3xl mx-auto space-y-3" data-id="element-2110">
                  <p data-id="element-2111">
                    Over{' '}
                    <span className="text-amber-400 font-bold" data-id="element-2112">
                      200 modules
                    </span>{' '}
                    covering{' '}
                    <span className="text-amber-400 font-bold" data-id="element-2113">
                      130+ peptides
                    </span>
                    . <span className="text-amber-400 font-bold" data-id="element-2114">57</span> of
                    the most powerful synergistic protocols ever designed. Full
                    coverage of{' '}
                    <span className="text-green-300 font-semibold" data-id="element-2115">
                      EVERY single Khavinson peptide bioregulator
                    </span>{' '}
                    — including all three versions. The power of peptide
                    bioaugmentors. More scientific references (over{' '}
                    <span className="text-amber-400 font-bold" data-id="element-2116">1,193</span>)
                    than any other biohacking program on earth.
                  </p>
                </div>

                {/* NOW reveal the name + price */}
                <div className="pt-2 border-t border-green-500/20" data-id="element-2117">
                  <p className="text-base sm:text-lg text-stone-400" data-id="element-2118">
                    This is{' '}
                    <span className="text-green-300 font-bold" data-id="element-2119">
                      Peptide Mastery 2.0
                    </span>{' '}
                    — normally{' '}
                    <span className="text-red-400 line-through font-semibold" data-id="element-2120">
                      $2,500
                    </span>{' '}
                    (pay-in-full only).{' '}
                    <span className="text-green-400 font-bold" data-id="element-2121">
                      Included FREE with your membership.
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Plus divider */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 2.2
          }} className="flex items-center justify-center my-6" data-id="element-2122">
              <div className="text-4xl sm:text-5xl font-bold text-amber-300" style={{
              textShadow: '0 0 15px rgba(251, 191, 36, 0.3)'
            }} data-id="element-2123">
                +
              </div>
            </motion.div>

            {/* Direct access announcement box */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 2.4
          }} className="p-6 sm:p-8 bg-gradient-to-br from-green-950/30 to-black/40 border-2 border-green-500/40 rounded-xl shadow-[0_0_40px_rgba(34,197,94,0.2)]" data-id="element-2124">
              <div className="text-center space-y-3 sm:space-y-4" data-id="element-2125">
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-300 leading-tight" style={{
                textShadow: '0 0 20px rgba(34, 197, 94, 0.3)'
              }} data-id="element-2126">
                  12 Months of Direct Access to the Masters Who Can "Do the
                  Impossible", Brenden Henry & Dr. Ali Mazloum
                </p>
                <p className="text-base sm:text-lg md:text-xl text-stone-300 font-semibold" data-id="element-2127">
                  <span className="text-red-400 line-through font-bold" data-id="element-2128">
                    Worth $18,000+
                  </span>{' '}
                  — <span className="text-green-400 font-bold" data-id="element-2129">INCLUDED</span>
                </p>
                <p className="text-sm sm:text-base text-green-300/80 italic" data-id="element-2130">
                  (It normally costs $1,500 an hour to gain private access to
                  either of them. As a member you will get continual guidance
                  throughout 2026 in the form of group sessions, access to
                  completely new dimensions of thinking about peptides,
                  pharmacology and biology AND a whole lot more)
                </p>
              </div>
            </motion.div>

            {/* Investment pricing box — THE CLIMAX */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 2.8
          }} className="mt-8 relative" data-id="element-2131">
              {/* Outer glow */}
              <motion.div className="absolute -inset-[3px] rounded-2xl opacity-50" animate={{
              boxShadow: ['0 0 30px rgba(217, 119, 6, 0.4), 0 0 60px rgba(217, 119, 6, 0.15)', '0 0 40px rgba(251, 191, 36, 0.5), 0 0 80px rgba(251, 191, 36, 0.2)', '0 0 30px rgba(217, 119, 6, 0.4), 0 0 60px rgba(217, 119, 6, 0.15)']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} style={{
              border: '2px solid rgba(217, 119, 6, 0.5)',
              borderRadius: '1rem'
            }} data-id="element-2132" />

              <div className="relative p-6 sm:p-8 md:p-10 bg-gradient-to-br from-amber-950/40 via-black/80 to-stone-950/40 rounded-2xl" data-id="element-2133">
                {/* Value stack recap */}
                <div className="text-center mb-6 sm:mb-8" data-id="element-2134">
                  <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-stone-500 mb-4" data-id="element-2135">
                    So let's add it up
                  </p>
                  <div className="space-y-2 max-w-md mx-auto" data-id="element-2136">
                    <div className="flex justify-between items-center text-sm sm:text-base text-stone-400 border-b border-stone-800/50 pb-2" data-id="element-2137">
                      <span data-id="element-2138">Peptide Mastery 2.0 (200+ modules)</span>
                      <span className="text-red-400 line-through font-semibold" data-id="element-2139">
                        $2,500
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm sm:text-base text-stone-400 border-b border-stone-800/50 pb-2" data-id="element-2140">
                      <span data-id="element-2141">12 Months Direct Access</span>
                      <span className="text-red-400 line-through font-semibold" data-id="element-2142">
                        $18,000+
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm sm:text-base text-stone-400 border-b border-stone-800/50 pb-2" data-id="element-2143">
                      <span data-id="element-2144">Monthly LIVE Research Briefings</span>
                      <span className="text-red-400 line-through font-semibold italic" data-id="element-2145">
                        Priceless
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total value */}
                <div className="text-center mb-6" data-id="element-2146">
                  <p className="text-base sm:text-lg text-stone-500 mb-1" data-id="element-2147">
                    Combined Real-World Value:
                  </p>
                  <p className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-red-400 line-through" style={{
                  textShadow: '0 0 15px rgba(239, 68, 68, 0.2)'
                }} data-id="element-2148">
                    $20,500+
                  </p>
                </div>

                {/* Dramatic divider */}
                <div className="flex items-center justify-center gap-3 my-6" data-id="element-2149">
                  <div className="w-20 h-px bg-gradient-to-r from-transparent to-amber-500/50" data-id="element-2150" />
                  <span className="text-amber-400 text-xs uppercase tracking-[0.3em] font-bold" data-id="element-2151">
                    Your Price
                  </span>
                  <div className="w-20 h-px bg-gradient-to-l from-transparent to-amber-500/50" data-id="element-2152" />
                </div>

                {/* THE PRICE — the wow moment */}
                <div className="text-center space-y-4" data-id="element-2153">
                  <motion.p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-amber-300" animate={{
                  textShadow: ['0 0 20px rgba(251, 191, 36, 0.3)', '0 0 40px rgba(251, 191, 36, 0.5)', '0 0 20px rgba(251, 191, 36, 0.3)']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} style={{
                  fontFamily: 'Cinzel, serif'
                }} data-id="element-2154">
                    Only $249 Today
                  </motion.p>
                  <p className="text-base sm:text-lg text-stone-400 -mt-1" data-id="element-2155">
                    Then 11 monthly payments of $249
                  </p>

                  {/* Annual option */}
                  <div className="pt-4 mt-2 border-t border-amber-900/30" data-id="element-2156">
                    <p className="text-lg sm:text-xl text-stone-300" data-id="element-2157">
                      Or pay{' '}
                      <span className="text-amber-400 font-bold text-xl sm:text-2xl" data-id="element-2158">
                        $2,490
                      </span>{' '}
                      in full today{' '}
                      <span className="text-green-400 font-semibold" data-id="element-2159">
                        (save $498)
                      </span>
                    </p>
                    <p className="text-sm sm:text-base text-green-400/90 font-medium mt-2" data-id="element-2160">
                      ✦ Annual members instantly unlock{' '}
                      <span className="font-bold text-green-300" data-id="element-2161">
                        ALL 200+ modules
                      </span>{' '}
                      of Peptide Mastery immediately
                    </p>
                  </div>

                  {/* Lock-in guarantee */}
                  <p className="text-xs sm:text-sm text-stone-500 italic pt-2" data-id="element-2162">
                    Lock in this rate for life — renew at the same price as long
                    as you stay
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CTA Button to scroll to pricing */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 2.8
          }} className="mt-8 flex justify-center" data-id="element-2163">
              <button onClick={() => {
              const pricingSection = document.querySelector('[data-pricing-section]');
              if (pricingSection) {
                pricingSection.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }} className="px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black text-lg sm:text-xl font-bold rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 min-h-[44px]" data-id="element-2164">
                Join the Private Research Order Now
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* DNA Helix Divider - Scientific but mystical */}
        <motion.div initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 2.2
      }} className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-2165">
          <motion.div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-600 to-amber-600" animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2166" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-2167">
            <svg width="24" height="24" viewBox="0 0 24 24" className="text-amber-500" data-id="element-2168">
              <circle cx="12" cy="6" r="2" fill="currentColor" opacity="0.8" data-id="element-2169" />
              <circle cx="12" cy="12" r="2" fill="currentColor" data-id="element-2170" />
              <circle cx="12" cy="18" r="2" fill="currentColor" opacity="0.8" data-id="element-2171" />
              <path d="M12 6 L12 18" stroke="currentColor" strokeWidth="1" opacity="0.4" data-id="element-2172" />
            </svg>
          </motion.div>
          <motion.div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-600 to-amber-600" animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2173" />
        </motion.div>

        {/* Target audience - Enhanced with better breaks and emphasis */}
        <motion.p initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 2.6
      }} className="text-lg sm:text-xl md:text-2xl text-stone-300 mb-10 sm:mb-16 leading-relaxed max-w-5xl mx-auto" data-id="element-2174">
          If you're a doctor, clinician, coach, practitioner,
          <br data-id="element-2175" />
          or an advanced biohacker ready to gain{' '}
          <span className="font-bold text-amber-400 px-2 py-1 border border-amber-500/30 bg-amber-950/30 rounded" style={{
          textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
        }} data-id="element-2176">
            REAL control
          </span>{' '}
          of human biology
          <br data-id="element-2177" />
          (for yourself or for others)...
        </motion.p>

        {/* DRAMATIC PAUSE - then the hook (NO BOX, NO BREATHING) */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: 3.5,
        type: 'spring',
        stiffness: 80
      }} className="mb-8 sm:mb-12 relative" data-id="element-2178">
          {/* Dramatic spotlight effect behind text */}
          <motion.div className="absolute inset-0 -inset-x-20 bg-gradient-radial from-amber-600/20 via-amber-600/5 to-transparent blur-3xl" animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2179" />

          {/* Subtle particle effects around the text */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-2180">
            {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-amber-400 rounded-full" style={{
            left: `${20 + i * 15}%`,
            top: '50%'
          }} animate={{
            y: [-20, -40, -20],
            opacity: [0, 1, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            delay: i * 0.5,
            ease: 'easeInOut'
          }} data-id="element-2181" />)}
          </div>

          <div className="relative text-center max-w-4xl mx-auto space-y-2 sm:space-y-4" data-id="element-2182">
            <motion.p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold" style={{
            background: 'linear-gradient(to right, #fbbf24, #f59e0b, #fbbf24)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 40px rgba(251, 191, 36, 0.4))'
          }} animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-2183">
              This could be the
            </motion.p>

            <motion.p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-amber-400" style={{
            textShadow: '0 0 60px rgba(251, 191, 36, 0.6)'
          }} data-id="element-2184">
              most life-changing opportunity
              <br data-id="element-2185" />
              you ever get
            </motion.p>
          </div>

          {/* Subtle underline accent */}
          <motion.div className="mx-auto mt-6 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent" style={{
          width: '60%'
        }} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          duration: 1,
          delay: 4,
          ease: 'easeOut'
        }} data-id="element-2186" />

          {/* Important note */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 4.2
        }} className="text-base sm:text-lg md:text-xl text-white mt-8 max-w-3xl mx-auto leading-relaxed" data-id="element-2187">
            PLEASE NOTE: The Private Research Order is not for "normal" people.
            This is for those ready to access the deepest secrets of health,
            performance, and regeneration beyond anything else you've seen.
          </motion.p>
        </motion.div>

        {/* TESTIMONIALS SECTION - Supporting Claims */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 4.2
      }} className="mt-12 sm:mt-20 mb-10 sm:mb-16 max-w-4xl mx-auto" data-id="element-2188">
          {/* Section intro - Purple accent */}
          <div className="text-center mb-10 sm:mb-16" data-id="element-2189">
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 4.4
          }} className="text-purple-400 text-lg sm:text-xl font-semibold mb-3" style={{
            textShadow: '0 0 20px rgba(192, 132, 252, 0.3)'
          }} data-id="element-2190">
              Real Results From Real Clients & Customers
            </motion.p>
            <motion.div className="w-32 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent mx-auto" initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 4.6
          }} data-id="element-2191" />
          </div>

          {/* Testimonials - Single Column, Stacked */}
          <div className="space-y-10 sm:space-y-16" data-id="element-2192">
            {/* Testimonial 1 - Sean L. */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 4.7
          }} className="relative" data-id="element-2193">
              {/* Subtle background glow - purple tint */}
              <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2194" />

              <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-2195">
                {/* Profile Image — Kademlia with grandparents */}
                <div className="flex justify-center mb-6" data-id="element-2196">
                  <div className="relative" data-id="element-2197">
                    <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2198" />
                    <img src="https://cdn.magicpatterns.com/uploads/rgJCmiiF9SFNcVA57YAorv/KademliaWithGrandparents-1.png" alt="Kademlia with grandparents" className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2199" />
                  </div>
                </div>

                {/* Large opening quote - purple */}
                <div className="mb-4 sm:mb-6" data-id="element-2200">
                  <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2201">
                    "
                  </span>
                </div>

                {/* TV Screen Frame around testimonial text */}
                <div className="relative mb-6 sm:mb-8 max-w-3xl" data-id="element-2202">
                  {/* Outer purple glow */}
                  <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2203" />

                  {/* TV screen frame */}
                  <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2204">
                    {/* Screen scanline effect (subtle) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2205" />

                    {/* Testimonial text */}
                    <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-2206">
                      <p data-id="element-2207">
                        Update on grandparents with peptides:{' '}
                        <span className="text-white font-bold" data-id="element-2208">
                          He just called me to update me he feels 20 years
                          younger
                        </span>{' '}
                        and she is doing yard work AND just did a{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2209">
                          100 piece jigsaw puzzle
                        </span>{' '}
                        (she's loved puzzles her whole life).
                      </p>
                      <p data-id="element-2210">
                        <span className="text-white font-bold" data-id="element-2211">
                          6 months ago she couldn't do a five piece jigsaw
                          puzzle.
                        </span>
                      </p>
                      <p data-id="element-2212">
                        I don't care what anyone says,{' '}
                        <span className="text-amber-400 font-extrabold" data-id="element-2213">
                          peptides f**king work.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Attribution with client type */}
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2214">
                  <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2215" />
                  <div className="text-center sm:text-right" data-id="element-2216">
                    <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2217">
                      Kademlia
                    </p>
                    <p className="text-stone-500 text-sm" data-id="element-2218">
                      Transformation Services Client & Peptide Mastery Buyer
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 2 - Wilfred A. */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 4.9
          }} className="relative" data-id="element-2219">
              {/* Subtle background glow - purple tint */}
              <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2220" />

              <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-2221">
                {/* Profile Image */}
                <div className="flex justify-center mb-6" data-id="element-2222">
                  <div className="relative" data-id="element-2223">
                    <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2224" />
                    <img src="https://cdn.magicpatterns.com/uploads/8tpyPupU8CaQsAzF9stSbx/WilfredA-2.jpg" alt="Wilfred A." className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2225" />
                  </div>
                </div>

                {/* Large opening quote - purple */}
                <div className="mb-4 sm:mb-6" data-id="element-2226">
                  <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2227">
                    "
                  </span>
                </div>

                {/* TV Screen Frame around testimonial text */}
                <div className="relative mb-6 sm:mb-8 max-w-3xl" data-id="element-2228">
                  {/* Outer purple glow */}
                  <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2229" />

                  {/* TV screen frame */}
                  <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2230">
                    {/* Screen scanline effect (subtle) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2231" />

                    {/* Testimonial text - with emphasis on key phrases */}
                    <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10" data-id="element-2232">
                      I{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-2233">
                        100% would put my name behind
                      </span>{' '}
                      Ali & Brenden. I've had other health coaches in the past
                      (most are MD's), and I must say, Brenden & Ali are{' '}
                      <span className="text-white font-bold" data-id="element-2234">
                        FAR more in depth, systems-oriented, and rigorous
                      </span>{' '}
                      than anything I've ever seen. Also I did grab that
                      peptides course. It has given me the knowledge to turn
                      familys' health conditions around even when the medical
                      system has failed them. I owe this to the lessons I've
                      acquired from Ali & Brenden. In terms of level of positive
                      impact on my life, they are likely in the{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-2235">
                        top 5 most important people I've ever met
                      </span>{' '}
                      as they have{' '}
                      <span className="text-white font-bold" data-id="element-2236">
                        added many years to my life
                      </span>{' '}
                      and to those of my loved ones.
                    </p>
                  </div>
                </div>

                {/* Attribution with client type */}
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2237">
                  <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2238" />
                  <div className="text-center sm:text-right" data-id="element-2239">
                    <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2240">
                      Wilfred A.
                    </p>
                    <p className="text-stone-500 text-sm" data-id="element-2241">
                      Peptide Mastery Owner & Transformation Services Client
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 3 - Ronald R. */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 5.1
          }} className="relative" data-id="element-2242">
              {/* Subtle background glow - purple tint */}
              <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2243" />

              <div className="relative px-4 sm:px-8 py-8 sm:py-10" data-id="element-2244">
                {/* Large opening quote - purple */}
                <div className="mb-4 sm:mb-6" data-id="element-2245">
                  <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2246">
                    "
                  </span>
                </div>

                {/* TV Screen Frame around testimonial text */}
                <div className="relative mb-6 sm:mb-8 max-w-3xl" data-id="element-2247">
                  {/* Outer purple glow */}
                  <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2248" />

                  {/* TV screen frame */}
                  <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2249">
                    {/* Screen scanline effect (subtle) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2250" />

                    {/* Testimonial text - with emphasis on key phrases */}
                    <p className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10" data-id="element-2251">
                      I am a{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-2252">
                        physician with an integrative medicine practice
                      </span>{' '}
                      and your work has been{' '}
                      <span className="text-white font-bold" data-id="element-2253">
                        very valuable in treating my patients.
                      </span>{' '}
                      When I lecture to physicians I always mention "peptide
                      mastery" as a great source of material and{' '}
                      <span className="text-amber-400 font-semibold" data-id="element-2254">
                        extensive references
                      </span>
                      ... It has been very helpful in my care of patients and I
                      appreciate the extensive references.
                    </p>
                  </div>
                </div>

                {/* Attribution with client type */}
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2255">
                  <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2256" />
                  <div className="text-center sm:text-right" data-id="element-2257">
                    <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2258">
                      Ronald R.
                    </p>
                    <p className="text-stone-500 text-sm" data-id="element-2259">
                      Peptide Mastery Owner
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* MASSIVE BREATHING ROOM - Let the claim sink in */}

        {/* For The Ones Who Already Know - COMPLETELY REDESIGNED OFFER BOX */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 4.3
      }} className="mb-8 sm:mb-12 relative" data-id="element-2260">
          {/* Distinctive outer glow to separate from page */}
          <div className="absolute -inset-4 bg-gradient-to-r from-amber-600/20 via-purple-600/20 to-cyan-600/20 blur-2xl rounded-lg" data-id="element-2261" />

          {/* Main offer box with SIMPLE BLACK background */}
          <div className="relative p-6 sm:p-10 md:p-12 bg-black border-4 border-amber-500/60 rounded-lg shadow-[0_0_80px_rgba(217,119,6,0.4)]" data-id="element-2262">
            {/* Decorative top accent */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-2263"></div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 4.3
          }} className="text-center mb-6 sm:mb-8" data-id="element-2264">
              <div className="inline-block px-4 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 border-2 border-amber-500/60 rounded-lg" data-id="element-2265">
                <p className="text-amber-300 text-lg sm:text-xl md:text-2xl font-bold tracking-wide" style={{
                textShadow: '0 0 20px rgba(252, 211, 77, 0.5)'
              }} data-id="element-2266">
                  SPECIAL PRO MEMBER OFFER
                </p>
              </div>
            </motion.div>

            {/* Celebratory Trophy Animation - Moved below offer box */}

            {/* Updated headline */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 4.5
          }} className="mt-4 sm:mt-6 mb-8 sm:mb-10" data-id="element-2267"></motion.div>

            {/* Divider */}
            <div className="flex items-center justify-center gap-4 my-8 sm:my-10" data-id="element-2268">
              <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2269" />
              <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-2270" />
              <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2271" />
            </div>

            {/* WHO THIS IS FOR Section */}
            <div className="mb-8 sm:mb-10" data-id="element-2272">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 5.2
            }} className="text-center mb-6 sm:mb-8" data-id="element-2273">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4" style={{
                textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
              }} data-id="element-2274">
                  👥 WHO THIS IS FOR
                </h3>
                <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-2275" />
              </motion.div>

              <div className="space-y-6 max-w-3xl mx-auto text-stone-300 mb-8 sm:mb-10" data-id="element-2276">
                <p className="text-base sm:text-lg text-center" data-id="element-2277">
                  Three groups:
                </p>

                <div className="space-y-4 sm:space-y-6" data-id="element-2278">
                  <div className="p-4 sm:p-6 bg-cyan-950/20 border-l-4 border-cyan-500 rounded-r-lg" data-id="element-2279">
                    <p className="font-bold text-lg sm:text-xl text-cyan-300 mb-2 sm:mb-3" data-id="element-2280">
                      1) Intelligent Biohackers
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base" data-id="element-2281">
                      For those who want total mastery over their own biology
                      (and the ability to help others with their health &
                      performance). Peptide Mastery 2.0, the monthly content and
                      the Q&A sessions can help you gain biological superpowers.
                    </p>
                  </div>

                  <div className="p-4 sm:p-6 bg-emerald-950/20 border-l-4 border-emerald-500 rounded-r-lg" data-id="element-2282">
                    <p className="font-bold text-lg sm:text-xl text-emerald-300 mb-2 sm:mb-3" data-id="element-2283">
                      2) Clinicians/Coaches/Practitioners
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base" data-id="element-2284">
                      For the professionals who want to be the "go-to" expert
                      and the ability to help almost ANYONE, even if everyone
                      else has failed. Peptide Mastery 2.0, the Q&A sessions,
                      Brenden's intel drops and particularly Dr. Ali's
                      diagnostics & medical work will take you to elite levels.
                    </p>
                  </div>

                  <div className="p-4 sm:p-6 bg-purple-950/20 border-l-4 border-purple-500 rounded-r-lg" data-id="element-2285">
                    <p className="font-bold text-lg sm:text-xl text-purple-300 mb-2 sm:mb-3" data-id="element-2286">
                      3) Smart People Who Want Access to the Best
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base" data-id="element-2287">
                      For those who simply want shortcuts to the best answers,
                      protocols, guidance and results whenever needed. Peptide
                      Mastery 2.0 with its masterful Dosing & Reference Sheet,
                      the 54 synergistic protocols, the modules covering every
                      part of the body, all the monthly content and the Q&A
                      sessions will allow you to find EXACTLY how get the best
                      results without needing to constantly study.
                    </p>
                  </div>
                </div>

                <div className="text-center space-y-3 sm:space-y-4 py-4 sm:py-6" data-id="element-2288">
                  <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-2289">
                    We're looking for leaders who can lead themselves AND lead
                    others.
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-2290">
                    People who are proactive, committed and take FULL
                    responsibility.
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-2291">
                    People who want to execute with certainty in a world full of
                    noise.
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-400 mt-4 sm:mt-6" style={{
                  textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
                }} data-id="element-2292">
                    This is the opportunity you've been waiting for.
                  </p>
                </div>
              </div>
            </div>

            {/* Divider - Added below "This is the opportunity you've been waiting for." */}
            <div className="flex items-center justify-center gap-4 my-8 sm:my-10" data-id="element-2293">
              <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2294" />
              <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-2295" />
              <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2296" />
            </div>

            {/* WHAT THIS IS Section */}
            <div className="mb-8 sm:mb-10" data-id="element-2297">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 5.15
            }} className="text-center mb-6 sm:mb-8" data-id="element-2298">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4 flex items-center justify-center gap-3" style={{
                textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
              }} data-id="element-2299">
                  <span className="text-3xl sm:text-4xl" data-id="element-2300">🧬</span> WHAT THIS IS
                </h3>
                <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-2301" />
              </motion.div>

              <div className="space-y-6 max-w-3xl mx-auto text-stone-300" data-id="element-2302">
                <p className="text-base sm:text-lg text-center" data-id="element-2303">
                  Right now, new for 2026: The Peptide Science Institute is
                  opening an{' '}
                  <span className="text-purple-400 font-bold" data-id="element-2304">
                    elite, exclusive underground intelligence initiative.
                  </span>
                </p>

                <div className="p-4 sm:p-6 bg-purple-950/20 border-l-4 border-purple-500 rounded-r-lg" data-id="element-2305">
                  <p className="text-lg sm:text-xl font-bold text-purple-300 mb-2 sm:mb-3 text-center" data-id="element-2306">
                    It's called the Private Research Order (PRO).
                  </p>
                  <p className="text-stone-300 text-center text-sm sm:text-base" data-id="element-2307">
                    And we're looking for the best people to join us as founding
                    members.
                  </p>
                </div>

                <div className="text-center space-y-3 sm:space-y-4 py-4 sm:py-6" data-id="element-2308">
                  <p className="text-lg sm:text-xl" data-id="element-2309">
                    <span className="text-amber-400 font-bold" data-id="element-2310">
                      Our mission:
                    </span>{' '}
                    To empower members with the awe-inspiring ability to{' '}
                    <span className="font-bold text-white" data-id="element-2311">
                      control human biology at the cellular level.
                    </span>
                  </p>
                  <p className="text-base sm:text-lg italic text-stone-400" data-id="element-2312">
                    Whether you want to heal, enhance performance, or optimize
                    at the highest levels.
                  </p>
                </div>

                <p className="text-xl sm:text-2xl font-bold text-white text-center" data-id="element-2313">
                  This is a complete intelligence immersion.
                </p>

                <p className="text-base sm:text-lg text-center" data-id="element-2314">
                  You get direct, ongoing access to the two masterminds who
                  decoded the peptide and biological matrix:
                </p>

                {/* Expert Cards - Side by Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 sm:mt-8" data-id="element-2315">
                  {/* Brenden Henry Card */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.5,
                  delay: 5.3
                }} className="p-4 sm:p-6 bg-gradient-to-br from-cyan-950/20 to-black border-2 border-cyan-500/60 rounded-lg" data-id="element-2316">
                    <div className="flex justify-center mb-4" data-id="element-2317">
                      <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center" data-id="element-2318">
                        <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20" data-id="element-2319">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-2320" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="text-2xl font-bold text-cyan-300 mb-2 text-center" data-id="element-2321">
                      Brenden Henry
                    </h4>
                    <p className="text-cyan-200 font-semibold text-center mb-4" data-id="element-2322">
                      WORLD'S #1 PEPTIDE AUTHORITY
                    </p>
                    <ul className="space-y-2 text-sm text-stone-300" data-id="element-2323">
                      <li className="flex items-start gap-2" data-id="element-2324">
                        <span className="text-cyan-400 mt-1" data-id="element-2325">•</span>
                        <span data-id="element-2326">
                          <span className="font-bold text-cyan-300" data-id="element-2327">
                            First & ONLY person
                          </span>{' '}
                          to translate every original Russian peptide experiment
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2328">
                        <span className="text-cyan-400 mt-1" data-id="element-2329">•</span>
                        <span data-id="element-2330">
                          Creator of{' '}
                          <span className="font-semibold text-cyan-300" data-id="element-2331">
                            Peptide Mastery 2.0
                          </span>{' '}
                          - the most advanced peptide education in history
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2332">
                        <span className="text-cyan-400 mt-1" data-id="element-2333">•</span>
                        <span data-id="element-2334">
                          Primary author of{' '}
                          <span className="italic text-cyan-300" data-id="element-2335">
                            Peptide Salvation
                          </span>{' '}
                          - the world's biggest book on peptides
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2336">
                        <span className="text-cyan-400 mt-1" data-id="element-2337">•</span>
                        <span data-id="element-2338">
                          Originator of the{' '}
                          <span className="font-semibold text-cyan-300" data-id="element-2339">
                            Henry Cofactor Principle
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2340">
                        <span className="text-cyan-400 mt-1" data-id="element-2341">•</span>
                        <span data-id="element-2342">
                          Identified the class of "super peptides" known as{' '}
                          <span className="font-semibold text-cyan-300" data-id="element-2343">
                            bioaugmentors
                          </span>
                        </span>
                      </li>
                    </ul>
                    <p className="text-cyan-300 font-bold text-center mt-4" data-id="element-2344">
                      No one else comes close.
                    </p>
                  </motion.div>

                  {/* Dr. Ali Mazloum Card */}
                  <motion.div initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.5,
                  delay: 5.4
                }} className="p-4 sm:p-6 bg-gradient-to-br from-emerald-950/20 to-black border-2 border-emerald-500/60 rounded-lg" data-id="element-2345">
                    <div className="flex justify-center mb-4" data-id="element-2346">
                      <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center" data-id="element-2347">
                        <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20" data-id="element-2348">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-2349" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="text-2xl font-bold text-emerald-300 mb-2 text-center" data-id="element-2350">
                      Dr. Ali Mazloum
                    </h4>
                    <p className="text-emerald-200 font-semibold text-center mb-4" data-id="element-2351">
                      "THE HUMAN MRI"
                    </p>
                    <ul className="space-y-2 text-sm text-stone-300" data-id="element-2352">
                      <li className="flex items-start gap-2" data-id="element-2353">
                        <span className="text-emerald-400 mt-1" data-id="element-2354">•</span>
                        <span data-id="element-2355">
                          <span className="font-bold text-emerald-300" data-id="element-2356">
                            Diagnostic genius
                          </span>{' '}
                          who spots what entire medical teams miss
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2357">
                        <span className="text-emerald-400 mt-1" data-id="element-2358">•</span>
                        <span data-id="element-2359">
                          Creates{' '}
                          <span className="font-semibold text-emerald-300" data-id="element-2360">
                            "impossible" transformations
                          </span>{' '}
                          that make specialists ask "how?"
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2361">
                        <span className="text-emerald-400 mt-1" data-id="element-2362">•</span>
                        <span data-id="element-2363">
                          Often idolized by other medical specialists at his
                          hospital
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2364">
                        <span className="text-emerald-400 mt-1" data-id="element-2365">•</span>
                        <span data-id="element-2366">
                          Head Transformation Specialist at the{' '}
                          <span className="font-semibold text-emerald-300" data-id="element-2367">
                            Peptide Science Institute
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2368">
                        <span className="text-emerald-400 mt-1" data-id="element-2369">•</span>
                        <span data-id="element-2370">
                          Complete{' '}
                          <span className="font-semibold text-emerald-300" data-id="element-2371">
                            "full stack"
                          </span>{' '}
                          master of the human body
                        </span>
                      </li>
                    </ul>
                    <p className="text-emerald-300 font-bold text-center mt-4" data-id="element-2372">
                      Any opportunity to learn from him is one you should take.
                    </p>
                  </motion.div>
                </div>

                <div className="text-center space-y-3 sm:space-y-4 py-4 sm:py-6 mt-6 sm:mt-8" data-id="element-2373">
                  <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-2374">
                    So, at this point you should realize...
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-2375">
                    This isn't some random online course with videos for you to
                    forget about.
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-400" style={{
                  textShadow: '0 0 20px rgba(251, 191, 36, 0.4)'
                }} data-id="element-2376">
                    This is an invitation to completely upgrade your life.
                  </p>
                  <p className="text-base sm:text-lg text-stone-300" data-id="element-2377">
                    With an elite, continual intelligence feed throughout 2026.
                  </p>
                  <p className="text-lg sm:text-xl font-bold text-white" data-id="element-2378">
                    A living, breathing command center.
                  </p>
                </div>
              </div>
            </div>

            {/* Divider before Where you get */}
            <div className="flex items-center justify-center gap-4 my-8 sm:my-10" data-id="element-2379">
              <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2380" />
              <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-2381" />
              <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2382" />
            </div>

            {/* WHERE YOU GET Section - Redesigned for visual flow */}
            <div className="mb-8 sm:mb-10" data-id="element-2383">
              <div className="text-center mb-6 sm:mb-8" data-id="element-2384">
                <p className="text-xl sm:text-2xl font-bold text-amber-400 mb-4" data-id="element-2385">
                  Where you get:
                </p>
                <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-2386" />
              </div>

              <div className="grid gap-4 sm:gap-6 max-w-4xl mx-auto" data-id="element-2387">
                {/* Benefit 1 */}
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 5.3
              }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-cyan-950/20 to-transparent border-l-4 border-cyan-500 rounded-r-lg" data-id="element-2388">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-cyan-500 rounded-full flex items-center justify-center text-black font-bold text-sm sm:text-base" data-id="element-2389">
                    1
                  </div>
                  <div data-id="element-2390">
                    <p className="font-bold text-base sm:text-lg text-cyan-300 mb-1 sm:mb-2" data-id="element-2391">
                      Direct, live access to Brenden Henry & Dr. Ali Mazloum
                      each month
                    </p>
                    <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2392">
                      The two people who literally wrote the book on peptides.
                      And who've reversed "irreversible" conditions more times
                      than I can count.
                    </p>
                  </div>
                </motion.div>

                {/* Benefit 2 */}
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 5.4
              }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-purple-950/20 to-transparent border-l-4 border-purple-500 rounded-r-lg" data-id="element-2393">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-purple-500 rounded-full flex items-center justify-center text-black font-bold text-sm sm:text-base" data-id="element-2394">
                    2
                  </div>
                  <div data-id="element-2395">
                    <p className="font-bold text-base sm:text-lg text-purple-300 mb-1 sm:mb-2" data-id="element-2396">
                      The Precision Cell™ Pro methodology
                    </p>
                    <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2397">
                      You'll get to learn the core transformation framework
                      Brenden, Ali and our team use to get incredible results
                      for our private clients – ones paying $15,000 to $120,000.
                    </p>
                  </div>
                </motion.div>

                {/* Benefit 3 */}
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.5,
                delay: 5.5
              }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-amber-950/20 to-transparent border-l-4 border-amber-500 rounded-r-lg" data-id="element-2398">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm sm:text-base" data-id="element-2399">
                    3
                  </div>
                  <div data-id="element-2400">
                    <p className="font-bold text-base sm:text-lg text-amber-300 mb-1 sm:mb-2" data-id="element-2401">
                      The complete Peptide Mastery 2.0 program
                    </p>
                    <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2402">
                      130+ peptides. 54 of the most advanced multi-peptide
                      protocols ever created. 1,193 scientific citations. The
                      highest level and most complete peptide education in the
                      world. This alone deserves an hour-long video explaining
                      how powerful the information is. It is genuinely a
                      masterwork. It normally costs $2,500 on its own.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* THREE-PILLAR DELIVERY MODEL */}
            <div className="mb-8 sm:mb-10 mt-10 sm:mt-14" data-id="element-2403">
              {/* Section Header */}
              <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8" data-id="element-2404">
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2405" />
                <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-2406" />
                <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2407" />
              </div>
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 5.6
            }} className="text-center mb-8 sm:mb-10" data-id="element-2408">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-3" style={{
                textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
              }} data-id="element-2409">
                  🏛️ THE THREE-PILLAR DELIVERY MODEL
                </h3>
                <p className="text-base sm:text-lg text-stone-400 max-w-2xl mx-auto" data-id="element-2410">
                  How your monthly intelligence is delivered
                </p>
                <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mt-4" data-id="element-2411" />
              </motion.div>

              {/* Three Pillar Cards */}
              <div className="grid grid-cols-1 gap-6 sm:gap-8 max-w-4xl mx-auto" data-id="element-2412">
                {/* PILLAR 1 — THE MAP */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 5.7
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-cyan-950/30 to-black/60 border-2 border-cyan-500/50 rounded-xl shadow-[0_0_30px_rgba(6,182,212,0.15)]" data-id="element-2413">
                  {/* Pillar number badge */}
                  <div className="absolute -top-4 left-6 sm:left-8" data-id="element-2414">
                    <div className="px-4 py-1.5 bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-full text-black text-xs sm:text-sm font-bold shadow-lg" data-id="element-2415">
                      PILLAR 1
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-2416">
                    <h4 className="text-xl sm:text-2xl font-bold text-cyan-300 mb-1" style={{
                    textShadow: '0 0 20px rgba(6, 182, 212, 0.3)'
                  }} data-id="element-2417">
                      Brenden Henry's Monthly Intelligence Drop
                    </h4>
                    <p className="text-amber-400 font-bold text-lg sm:text-xl mb-4 tracking-wide" style={{
                    fontFamily: 'Cinzel, serif'
                  }} data-id="element-2418">
                      — THE MAP
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base leading-relaxed" data-id="element-2419">
                      Brenden operates at a level of understanding most people
                      never experience. He maps the complete landscape of what's
                      possible with peptides, shows you the latest research,
                      demonstrates Precision Cell in action, and shows you what
                      99% of practitioners miss.{' '}
                      <span className="text-cyan-300 font-semibold" data-id="element-2420">
                        This is where you gain mastery.
                      </span>
                    </p>
                  </div>
                </motion.div>

                {/* PILLAR 2 — THE ROUTE */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 5.85
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-emerald-950/30 to-black/60 border-2 border-emerald-500/50 rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.15)]" data-id="element-2421">
                  <div className="absolute -top-4 left-6 sm:left-8" data-id="element-2422">
                    <div className="px-4 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-full text-black text-xs sm:text-sm font-bold shadow-lg" data-id="element-2423">
                      PILLAR 2
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-2424">
                    <h4 className="text-xl sm:text-2xl font-bold text-emerald-300 mb-1" style={{
                    textShadow: '0 0 20px rgba(16, 185, 129, 0.3)'
                  }} data-id="element-2425">
                      Dr. Ali Mazloum's Live Strategic Briefing
                    </h4>
                    <p className="text-amber-400 font-bold text-lg sm:text-xl mb-4 tracking-wide" style={{
                    fontFamily: 'Cinzel, serif'
                  }} data-id="element-2426">
                      — THE ROUTE
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base leading-relaxed" data-id="element-2427">
                      Clarity. Confidence. Direction. Life-changing insight. Ali
                      can take you from elite-level enhancement to
                      pathology-specific interventions, all the way to urgent
                      on-demand solutions.{' '}
                      <span className="text-emerald-300 font-semibold" data-id="element-2428">
                        This is where you gain the ability to know exactly what
                        to do in almost any situation.
                      </span>
                    </p>
                  </div>
                </motion.div>

                {/* PILLAR 3 — THE APPLICATION */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 6.0
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-amber-950/30 to-black/60 border-2 border-amber-500/50 rounded-xl shadow-[0_0_30px_rgba(217,119,6,0.15)]" data-id="element-2429">
                  <div className="absolute -top-4 left-6 sm:left-8" data-id="element-2430">
                    <div className="px-4 py-1.5 bg-gradient-to-r from-amber-600 to-amber-500 rounded-full text-black text-xs sm:text-sm font-bold shadow-lg" data-id="element-2431">
                      PILLAR 3
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-2432">
                    <h4 className="text-xl sm:text-2xl font-bold text-amber-300 mb-1" style={{
                    textShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
                  }} data-id="element-2433">
                      The Double Live HOTSEAT Q&A
                    </h4>
                    <p className="text-amber-400 font-bold text-lg sm:text-xl mb-4 tracking-wide" style={{
                    fontFamily: 'Cinzel, serif'
                  }} data-id="element-2434">
                      — THE APPLICATION
                    </p>
                    <p className="text-stone-300 text-sm sm:text-base leading-relaxed" data-id="element-2435">
                      Both Brenden and Dr. Ali team up live to answer YOUR
                      questions and give you exact directions for your
                      situation. It normally costs{' '}
                      <span className="text-amber-300 font-semibold" data-id="element-2436">
                        $1,500
                      </span>{' '}
                      to access ONE of them for an hour.{' '}
                      <span className="text-amber-300 font-semibold" data-id="element-2437">
                        Our last Q&A session with BOTH of them went for over
                        FOUR hours.
                      </span>
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Bold Summary Line */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.15
            }} className="mt-8 sm:mt-10 text-center max-w-3xl mx-auto" data-id="element-2438">
                <div className="p-5 sm:p-6 bg-gradient-to-r from-amber-950/40 via-amber-900/20 to-amber-950/40 border border-amber-500/40 rounded-lg" data-id="element-2439">
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-amber-300 leading-relaxed" style={{
                  textShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
                }} data-id="element-2440">
                    Brenden gives you the complete map, Ali gives you the
                    precise route, and the live Q&A shows you how to apply both
                    to your specific situation.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* COSMIC TEXT SECTION - INSIDE OFFER BOX */}
            <div className="border-t-2 border-cyan-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2441">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              delay: 6.05
            }} className="mb-8 sm:mb-10 max-w-5xl mx-auto relative" data-id="element-2442">
                {/* Dramatic spotlight behind text */}
                <div className="absolute inset-0 -inset-x-32 bg-gradient-radial from-cyan-600/10 via-transparent to-transparent blur-3xl" data-id="element-2443" />

                {/* Cosmic background - NO BOX, just floating elements */}
                <div className="relative py-8 sm:py-12 px-4 sm:px-6 overflow-visible" data-id="element-2444">
                  {/* Animated Stars - floating freely */}
                  <div className="absolute inset-0 -inset-x-20 -inset-y-20 overflow-visible pointer-events-none" data-id="element-2445">
                    {[...Array(40)].map((_, i) => <motion.div key={i} className="absolute bg-cyan-300 rounded-full" style={{
                    width: Math.random() * 3 + 1 + 'px',
                    height: Math.random() * 3 + 1 + 'px',
                    top: Math.random() * 120 - 10 + '%',
                    left: Math.random() * 120 - 10 + '%',
                    boxShadow: '0 0 4px rgba(103, 232, 249, 0.6)'
                  }} animate={{
                    opacity: [0.2, 0.7, 0.2],
                    scale: [0.8, 1.3, 0.8]
                  }} transition={{
                    duration: Math.random() * 3 + 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                    ease: 'easeInOut'
                  }} data-id="element-2446" />)}
                  </div>

                  {/* Constellation Lines - subtle connections */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20 overflow-visible" data-id="element-2447">
                    <motion.path d="M100,30 L250,90 L400,50 L600,100" stroke="#22d3ee" strokeWidth="1" fill="none" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} animate={{
                    pathLength: 1,
                    opacity: 0.3
                  }} transition={{
                    duration: 5,
                    ease: 'easeOut',
                    delay: 1
                  }} data-id="element-2448" />
                    <motion.path d="M50,100 L200,40 L350,110 L550,60" stroke="#67e8f9" strokeWidth="1" fill="none" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} animate={{
                    pathLength: 1,
                    opacity: 0.25
                  }} transition={{
                    duration: 6,
                    ease: 'easeOut',
                    delay: 2
                  }} data-id="element-2449" />
                  </svg>

                  {/* The Cosmic Text - NO BOX, bold font, floating in space */}
                  <div className="relative z-10 text-center space-y-4 sm:space-y-6" data-id="element-2450">
                    <motion.p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-relaxed" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 2,
                    delay: 0.5
                  }} data-id="element-2451">
                      <span className="block text-cyan-200 font-extrabold" style={{
                      textShadow: '0 0 15px rgba(103, 232, 249, 0.5), 0 0 30px rgba(103, 232, 249, 0.3)'
                    }} data-id="element-2452">
                        Gain access to whole new worlds,
                      </span>
                      <span className="block mt-4 text-cyan-100 font-extrabold" style={{
                      textShadow: '0 0 18px rgba(34, 211, 238, 0.6), 0 0 35px rgba(34, 211, 238, 0.4)'
                    }} data-id="element-2453">
                        different dimensions of thinking,
                      </span>
                      <span className="block mt-4 text-white font-extrabold" style={{
                      textShadow: '0 0 20px rgba(255, 255, 255, 0.5), 0 0 40px rgba(103, 232, 249, 0.4)'
                    }} data-id="element-2454">
                        and new paradigms for medicine,
                      </span>
                      <span className="block mt-4 text-cyan-200 font-extrabold" style={{
                      textShadow: '0 0 15px rgba(103, 232, 249, 0.5), 0 0 30px rgba(103, 232, 249, 0.3)'
                    }} data-id="element-2455">
                        biology and peptides
                      </span>
                    </motion.p>
                  </div>

                  {/* Shooting Stars - multiple for more cosmic feel */}
                  <motion.div className="absolute top-10 left-0 h-px w-32 bg-gradient-to-r from-transparent via-cyan-300 to-transparent" style={{
                  rotate: '25deg',
                  filter: 'drop-shadow(0 0 8px #22d3ee)'
                }} animate={{
                  x: ['-100%', '400%'],
                  y: ['-100%', '400%'],
                  opacity: [0, 1, 0]
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  repeatDelay: 8,
                  ease: 'easeOut'
                }} data-id="element-2456" />
                  <motion.div className="absolute bottom-20 right-0 h-px w-24 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" style={{
                  rotate: '-30deg',
                  filter: 'drop-shadow(0 0 6px #67e8f9)'
                }} animate={{
                  x: ['100%', '-400%'],
                  y: ['100%', '-400%'],
                  opacity: [0, 1, 0]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 12,
                  ease: 'easeOut',
                  delay: 4
                }} data-id="element-2457" />
                </div>
              </motion.div>
            </div>

            {/* BUT MAKE SURE YOU UNDERSTAND Section - MOVED HERE */}

            {/* OR IF YOU'RE READY Section */}
            <div className="border-t-2 border-green-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2458">
              {/* Jim C. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.25
            }} className="relative mb-16" data-id="element-2459">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2460" />

                <div className="relative px-4 sm:px-8 py-2 sm:py-3" data-id="element-2461">
                  {/* Profile Image */}
                  <div className="flex justify-center mb-2" data-id="element-2462">
                    <div className="relative" data-id="element-2463">
                      <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2464" />
                      <img src="https://cdn.magicpatterns.com/uploads/trgTFAse3SdhPusxQV6tPb/JimCox-2.jpg" alt="Jim C." className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2465" />
                    </div>
                  </div>

                  {/* Large opening quote - purple */}
                  <div className="-mb-4 sm:-mb-6" data-id="element-2466">
                    <span className="text-purple-500/40 text-5xl sm:text-7xl font-serif leading-none" data-id="element-2467">
                      "
                    </span>
                  </div>

                  {/* Testimonial frame */}
                  <div className="relative mb-4 sm:mb-5 max-w-3xl mx-auto" data-id="element-2468">
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2469" />

                    <div className="relative border-2 border-purple-500/30 rounded-lg p-4 sm:p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2470">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2471" />

                      <div className="text-stone-300 text-base sm:text-lg md:text-xl leading-relaxed relative z-10 space-y-4" data-id="element-2472">
                        <p data-id="element-2473">
                          Since talking to Dr. Ali about 8 months ago I started
                          my peptide regimen that was recommended to me based on
                          having{' '}
                          <span className="text-white font-bold" data-id="element-2474">
                            high cholesterol, low HDL, high LDL
                          </span>{' '}
                          and having an{' '}
                          <span className="text-white font-bold" data-id="element-2475">
                            ejection fraction of 35%
                          </span>{' '}
                          (cardiomyopathy dx.)
                        </p>

                        <p data-id="element-2476">
                          Just recently I had a blood panel done, echo of the
                          heart and an angiogram for my arteries.
                        </p>

                        <div className="bg-green-950/30 border-l-4 border-green-500/50 p-4 sm:p-5 rounded-r-lg my-2" data-id="element-2477">
                          <p className="text-green-400 font-bold text-lg sm:text-xl mb-3" data-id="element-2478">
                            RESULTS:
                          </p>
                          <div className="space-y-2" data-id="element-2479">
                            <p data-id="element-2480">
                              My cholesterol dropped from{' '}
                              <span className="text-amber-400 font-semibold" data-id="element-2481">
                                250 to 150
                              </span>{' '}
                              and LDL and HDL are now{' '}
                              <span className="text-green-400 font-semibold" data-id="element-2482">
                                within normal limits.
                              </span>
                            </p>
                            <p data-id="element-2483">
                              My ejection fraction is now at{' '}
                              <span className="text-amber-400 font-semibold" data-id="element-2484">
                                60%.
                              </span>
                            </p>
                            <p data-id="element-2485">
                              I have{' '}
                              <span className="text-green-400 font-bold" data-id="element-2486">
                                ZERO plaque build up
                              </span>{' '}
                              in my arteries.
                            </p>
                          </div>
                        </div>

                        <p data-id="element-2487">
                          My cardiologist was extremely surprised calling it{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2488">
                            a borderline miracle in 6 months.
                          </span>{' '}
                          In fact he said my{' '}
                          <span className="text-white font-bold" data-id="element-2489">
                            cardiomyopathy is now resolved
                          </span>{' '}
                          and I no longer have that diagnosis.
                        </p>

                        <p data-id="element-2490">
                          I want to{' '}
                          <span className="text-amber-400 font-semibold" data-id="element-2491">
                            thank Dr. Ali and the whole team
                          </span>{' '}
                          as I plan to continue my life using daily peptides.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Attribution */}
                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2492">
                    <div className="h-px w-full sm:w-auto sm:flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2493" />
                    <div className="text-center sm:text-right" data-id="element-2494">
                      <p className="text-purple-400 font-semibold text-base sm:text-lg mb-1" data-id="element-2495">
                        Jim C.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2496">
                        Meeting With the Master Client
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Michelle M.N. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.9
            }} className="relative mt-16" data-id="element-2497">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2498" />
              </motion.div>
            </div>

            {/* SOPHISTICATED PRICING SECTION WITH STRONG ANCHORS */}
            <div className="border-t-2 border-amber-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2499">
              {/* AMPLIFIED FOUNDING MEMBER MESSAGING */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 6.5
            }} className="text-center mb-12 relative" data-id="element-2500">
                {/* Dramatic glow effect */}
                <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-2501" />

                <div className="relative space-y-4 sm:space-y-6" data-id="element-2502">
                  {/* Main headline with intense emphasis */}
                  <motion.div animate={{
                  textShadow: ['0 0 40px rgba(251, 191, 36, 0.6)', '0 0 60px rgba(251, 191, 36, 0.9)', '0 0 40px rgba(251, 191, 36, 0.6)']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-2503">
                    <h3 className="text-2xl sm:text-4xl md:text-5xl font-bold text-amber-400 mb-3 sm:mb-4" data-id="element-2504">
                      🔥 PRO MEMBER EXCLUSIVE 🔥
                    </h3>
                  </motion.div>

                  <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4" data-id="element-2505">
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100 leading-tight" data-id="element-2506">
                      This is one of the{' '}
                      <span className="text-amber-400" data-id="element-2507">most insane offers</span>{' '}
                      we will ever make
                    </p>

                    <p className="text-lg sm:text-xl text-stone-300 leading-relaxed" data-id="element-2508">
                      We're opening the doors to our{' '}
                      <span className="font-bold text-stone-100" data-id="element-2509">
                        Private Research Order
                      </span>{' '}
                      — and looking for the{' '}
                      <span className="font-bold text-stone-100" data-id="element-2510">
                        absolute best people
                      </span>{' '}
                      to join as PRO members.
                    </p>

                    <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-2511">
                      Lock in your{' '}
                      <span className="text-green-400" data-id="element-2512">
                        grandfathered PRO Member rate
                      </span>{' '}
                      now — and keep it for life.
                    </p>

                    <p className="text-base sm:text-lg text-stone-400 italic" data-id="element-2513">
                      (As long as you remain a member, your rate never
                      increases)
                    </p>
                  </div>

                  {/* Celebratory Trophy Animation */}
                  <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 6.55
                }} className="my-8 flex justify-center" data-id="element-2514">
                    <div className="relative w-48 h-32 md:w-64 md:h-40" data-id="element-2515">
                      {/* Trophy/Award Icon */}
                      <motion.svg className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 md:w-24 md:h-24" viewBox="0 0 24 24" fill="none" animate={{
                      y: [0, -8, 0],
                      rotate: [0, 5, 0, -5, 0]
                    }} transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} data-id="element-2516">
                        {/* Trophy base */}
                        <path d="M12 15C15.866 15 19 11.866 19 8V5H5V8C5 11.866 8.13401 15 12 15Z" fill="url(#trophy-gradient)" stroke="#f59e0b" strokeWidth="1.5" data-id="element-2517" />
                        {/* Trophy stem */}
                        <path d="M12 15V19M9 19H15M9 22H15" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" data-id="element-2518" />
                        {/* Trophy handles */}
                        <path d="M5 8H3C2.44772 8 2 7.55228 2 7V6C2 5.44772 2.44772 5 3 5H5" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" data-id="element-2519" />
                        <path d="M19 8H21C21.5523 8 22 7.55228 22 7V6C22 5.44772 21.5523 5 21 5H19" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" data-id="element-2520" />
                        <defs data-id="element-2521">
                          <linearGradient id="trophy-gradient" x1="12" y1="5" x2="12" y2="15" data-id="element-2522">
                            <stop offset="0%" stopColor="#fbbf24" data-id="element-2523" />
                            <stop offset="100%" stopColor="#f59e0b" data-id="element-2524" />
                          </linearGradient>
                        </defs>
                      </motion.svg>

                      {/* Animated Confetti Particles */}
                      {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute" style={{
                      left: '50%',
                      top: '50%',
                      width: i % 3 === 0 ? '8px' : '6px',
                      height: i % 3 === 0 ? '8px' : '6px'
                    }} animate={{
                      x: [0, Math.cos(i * Math.PI * 2 / 12) * 80],
                      y: [0, Math.sin(i * Math.PI * 2 / 12) * 60],
                      rotate: [0, 360],
                      opacity: [1, 0]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: 'easeOut'
                    }} data-id="element-2525">
                          <div className={`w-full h-full rounded-full ${i % 4 === 0 ? 'bg-amber-400' : i % 4 === 1 ? 'bg-yellow-300' : i % 4 === 2 ? 'bg-amber-500' : 'bg-orange-400'}`} style={{
                        boxShadow: '0 0 8px currentColor'
                      }} data-id="element-2526" />
                        </motion.div>)}

                      {/* Sparkle Effects */}
                      {[...Array(6)].map((_, i) => <motion.div key={`sparkle-${i}`} className="absolute" style={{
                      left: `${20 + i * 15}%`,
                      top: `${30 + i % 2 * 20}%`
                    }} animate={{
                      scale: [0, 1, 0],
                      rotate: [0, 180],
                      opacity: [0, 1, 0]
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: 'easeInOut'
                    }} data-id="element-2527">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-amber-400" data-id="element-2528">
                            <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z" fill="currentColor" data-id="element-2529" />
                          </svg>
                        </motion.div>)}
                    </div>
                  </motion.div>

                  {/* Divider with emphasis */}
                  <div className="flex items-center justify-center gap-4 my-8" data-id="element-2530">
                    <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2531" />
                    <div className="w-4 h-4 bg-amber-500 rotate-45 animate-pulse" data-id="element-2532" />
                    <div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2533" />
                  </div>

                  {/* THE "INSANE" PART Section - MOVED HERE */}
                  <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 5.9
                }} className="text-center mb-12" data-id="element-2534">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4" style={{
                    textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
                  }} data-id="element-2535">
                      🎁 THE "INSANE" PART
                    </h3>
                    <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mb-8" data-id="element-2536" />

                    <div className="space-y-6 max-w-3xl mx-auto" data-id="element-2537">
                      <p className="text-lg font-bold text-stone-100 underline decoration-amber-400 decoration-2 underline-offset-4" data-id="element-2538">
                        If you are accepted into the Order we will grant you a
                        complete scholarship to Peptide Mastery 2.0.
                      </p>

                      <div className="p-6 bg-amber-950/30 border-2 border-amber-500/40 rounded-lg" data-id="element-2539">
                        <p className="text-lg text-stone-300 mb-2" data-id="element-2540">
                          This is the most comprehensive peptide education ever
                          created.
                        </p>
                        <p className="text-2xl font-bold text-amber-400" data-id="element-2541">
                          Normally, it's $2,500 (pay-in-full ONLY).
                        </p>
                        <p className="text-stone-400 mt-2" data-id="element-2542">
                          We don't offer payment plans.
                        </p>
                      </div>

                      <div className="space-y-3" data-id="element-2543">
                        <p className="text-2xl font-bold text-amber-300" data-id="element-2544">
                          But as a PRO Member?
                        </p>
                        <p className="text-3xl font-bold text-green-400" style={{
                        textShadow: '0 0 30px rgba(74, 222, 128, 0.5)'
                      }} data-id="element-2545">
                          You get it FREE.
                        </p>
                      </div>

                      <p className="text-lg text-stone-300" data-id="element-2546">
                        Plus you're locking in a grandfathered monthly rate that
                        we'll NEVER offer again.
                      </p>

                      <p className="text-stone-400 italic text-sm" data-id="element-2547">
                        (Once enrollment closes, this pricing disappears
                        forever.)
                      </p>
                    </div>
                  </motion.div>

                  <p className="text-2xl font-bold text-amber-400" data-pricing-section style={{
                  scrollMarginTop: '80px'
                }} data-id="element-2548">
                    Choose Your PRO Member Path Below:
                  </p>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-2549">
                {/* Option 1: Pay in Full — BEST VALUE (emphasized) */}
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 6.6
              }} className="relative" data-id="element-2550">
                  {/* Animated outer glow border */}
                  <motion.div className="absolute -inset-[3px] rounded-xl opacity-70" animate={{
                  boxShadow: ['0 0 25px rgba(34, 197, 94, 0.4), 0 0 50px rgba(217, 119, 6, 0.2)', '0 0 35px rgba(34, 197, 94, 0.6), 0 0 70px rgba(217, 119, 6, 0.3)', '0 0 25px rgba(34, 197, 94, 0.4), 0 0 50px rgba(217, 119, 6, 0.2)']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} style={{
                  border: '3px solid rgba(34, 197, 94, 0.5)',
                  borderRadius: '0.75rem',
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(217, 119, 6, 0.05))'
                }} data-id="element-2551" />

                  <div className="relative p-6 sm:p-8 bg-gradient-to-br from-amber-900/40 via-green-950/20 to-black border-3 border-amber-500 rounded-xl shadow-[0_0_40px_rgba(217,119,6,0.3)] hover:shadow-[0_0_60px_rgba(217,119,6,0.5)] transition-all" data-id="element-2552">
                    {/* Best Value Badge — prominent and animated */}
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10" data-id="element-2553">
                      <div className="px-5 sm:px-8 py-2.5 bg-gradient-to-r from-green-600 to-green-500 rounded-full text-white text-sm sm:text-base font-bold shadow-[0_4px_15px_rgba(34,197,94,0.4)] whitespace-nowrap" data-id="element-2554">
                        ⭐ MOST POPULAR OPTION ⭐
                      </div>
                    </div>

                    <div className="text-center mb-6 mt-6" data-id="element-2555">
                      <p className="text-amber-400 font-semibold text-base sm:text-lg mb-2" data-id="element-2556">
                        Option 1
                      </p>
                      <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-100 mb-4 leading-tight" data-id="element-2557">
                        Peptide Mastery 2.0 + PSI PRO{' '}
                        <span className="underline decoration-green-400 decoration-2 underline-offset-4" data-id="element-2558">
                          Annual
                        </span>{' '}
                        Membership
                      </h4>

                      {/* STRONG PRICE ANCHOR */}
                      <div className="mb-6 p-4 bg-red-950/30 border border-red-500/30 rounded-lg" data-id="element-2559">
                        <p className="text-red-400 text-xs sm:text-sm font-semibold mb-1" data-id="element-2560">
                          Normal Value:
                        </p>
                        <div className="flex items-center justify-center gap-3" data-id="element-2561">
                          <span className="text-2xl sm:text-3xl font-bold text-red-400 line-through opacity-60" data-id="element-2562">
                            $20,500+
                          </span>
                        </div>
                      </div>

                      <div className="mb-4" data-id="element-2563">
                        <p className="text-green-400 text-xs sm:text-sm font-bold mb-2" data-id="element-2564">
                          Your PRO Member Price:
                        </p>
                        <div className="flex items-baseline justify-center gap-2" data-id="element-2565">
                          <span className="text-4xl sm:text-5xl font-bold text-amber-400" data-id="element-2566">
                            $2,490
                          </span>
                        </div>
                        <p className="text-green-400 font-bold text-base sm:text-lg mt-2" data-id="element-2567">
                          Save over $18,000!
                        </p>
                        <p className="text-stone-400 text-xs sm:text-sm mt-1" data-id="element-2568">
                          Annual payment
                        </p>
                      </div>
                    </div>

                    {/* ⚡ INSTANT ACCESS CALLOUT — Key differentiator */}
                    <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-green-950/40 via-green-900/30 to-green-950/40 border-2 border-green-500/50 rounded-lg relative overflow-hidden" data-id="element-2569">
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-green-400/10 to-green-500/5" data-id="element-2570" />
                      <div className="relative text-center space-y-2" data-id="element-2571">
                        <p className="text-green-400 font-bold text-base sm:text-lg flex items-center justify-center gap-2" data-id="element-2572">
                          <span className="text-xl" data-id="element-2573">⚡</span> INSTANT FULL
                          ACCESS <span className="text-xl" data-id="element-2574">⚡</span>
                        </p>
                        <p className="text-stone-200 text-sm sm:text-base font-semibold" data-id="element-2575">
                          All{' '}
                          <span className="text-green-300 font-bold" data-id="element-2576">
                            200 modules
                          </span>{' '}
                          of Peptide Mastery 2.0 unlocked{' '}
                          <span className="text-green-300 font-bold underline decoration-green-400 underline-offset-2" data-id="element-2577">
                            immediately
                          </span>{' '}
                          upon enrollment
                        </p>
                        <p className="text-stone-400 text-xs sm:text-sm italic" data-id="element-2578">
                          No waiting. No drip. The entire course is yours from
                          day one.
                        </p>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8" data-id="element-2579">
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2580">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2581">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2582" />
                        </svg>
                        <span data-id="element-2583">
                          Full Peptide Mastery 2.0 —{' '}
                          <span className="font-bold text-green-300" data-id="element-2584">
                            ALL 200 modules unlocked immediately
                          </span>{' '}
                          <span className="text-stone-400" data-id="element-2585">($2,500 value)</span>
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2586">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2587">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2588" />
                        </svg>
                        <span data-id="element-2589">
                          12 months of PSI PRO membership{' '}
                          <span className="text-stone-400" data-id="element-2590">($3,000 value)</span>
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2591">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2592">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2593" />
                        </svg>
                        <span data-id="element-2594">All live sessions, bonuses, case studies</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2595">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2596">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2597" />
                        </svg>
                        <span data-id="element-2598">
                          <span className="font-semibold text-green-400" data-id="element-2599">
                            Save $498
                          </span>{' '}
                          vs monthly plan
                        </span>
                      </li>
                    </ul>

                    <a href="https://go.peptidescienceinstitute.com/pm-pro-annual-fm" className="block w-full px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black text-center font-bold text-base md:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 min-h-[44px]" data-id="element-2600">
                      Apply Now — Annual Membership
                    </a>
                  </div>
                </motion.div>

                {/* Option 2: Monthly Payments */}
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 6.7
              }} className="relative p-6 sm:p-8 bg-gradient-to-br from-stone-900/60 to-black border-2 border-amber-500/40 rounded-xl shadow-[0_0_20px_rgba(217,119,6,0.15)] hover:shadow-[0_0_40px_rgba(217,119,6,0.3)] transition-all" data-id="element-2601">
                  <div className="text-center mb-6" data-id="element-2602">
                    <p className="text-amber-400/80 font-semibold text-base sm:text-lg mb-2" data-id="element-2603">
                      Option 2
                    </p>
                    <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-100 mb-4 leading-tight" data-id="element-2604">
                      Peptide Mastery 2.0 + PSI PRO{' '}
                      <span className="underline" data-id="element-2605">Monthly</span> Membership
                    </h4>

                    {/* STRONG PRICE ANCHOR */}
                    <div className="mb-6 p-4 bg-red-950/30 border border-red-500/30 rounded-lg" data-id="element-2606">
                      <p className="text-red-400 text-xs sm:text-sm font-semibold mb-1" data-id="element-2607">
                        Normal Value:
                      </p>
                      <div className="flex items-center justify-center gap-3" data-id="element-2608">
                        <span className="text-2xl sm:text-3xl font-bold text-red-400 line-through opacity-60" data-id="element-2609">
                          $20,500+
                        </span>
                      </div>
                    </div>

                    <div className="mb-4" data-id="element-2610">
                      <p className="text-green-400 text-xs sm:text-sm font-bold mb-2" data-id="element-2611">
                        Your PRO Member Price:
                      </p>
                      <div className="flex items-baseline justify-center gap-2" data-id="element-2612">
                        <span className="text-4xl sm:text-5xl font-bold text-amber-400" data-id="element-2613">
                          $249
                        </span>
                        <span className="text-2xl text-stone-400" data-id="element-2614">today</span>
                      </div>
                      <p className="text-stone-300 font-medium text-base mt-2" data-id="element-2615">
                        Then 11 monthly payments of $249
                      </p>
                      <p className="text-green-400 font-bold text-sm mt-1" data-id="element-2616">
                        Save over $17,500!
                      </p>
                      <p className="text-stone-400 text-xs sm:text-sm mt-2" data-id="element-2617">
                        Total: $2,988
                      </p>
                    </div>
                  </div>

                  {/* Monthly drip note */}
                  <div className="mb-6 p-3 sm:p-4 bg-amber-950/20 border border-amber-500/20 rounded-lg" data-id="element-2618">
                    <div className="text-center space-y-1.5" data-id="element-2619">
                      <p className="text-amber-300/90 font-semibold text-sm sm:text-base" data-id="element-2620">
                        📚 Peptide Mastery 2.0 — Monthly Access
                      </p>
                      <p className="text-stone-400 text-xs sm:text-sm" data-id="element-2621">
                        Course modules are released in{' '}
                        <span className="text-stone-300 font-medium" data-id="element-2622">
                          12 monthly installments
                        </span>{' '}
                        matching your payment schedule
                      </p>
                      <p className="text-stone-500 text-xs italic" data-id="element-2623">
                        (Want all 200 modules immediately? Choose the Annual
                        option)
                      </p>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8" data-id="element-2624">
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2625">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2626">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2627" />
                      </svg>
                      <span data-id="element-2628">
                        Peptide Mastery 2.0 —{' '}
                        <span className="font-semibold text-amber-300" data-id="element-2629">
                          unlocked in 12 monthly modules
                        </span>{' '}
                        <span className="text-stone-400" data-id="element-2630">($2,500 value)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2631">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2632">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2633" />
                      </svg>
                      <span data-id="element-2634">
                        12 months of PSI PRO membership{' '}
                        <span className="text-stone-400" data-id="element-2635">($3,000 value)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2636">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2637">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2638" />
                      </svg>
                      <span data-id="element-2639">All live sessions, bonuses, case studies</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-stone-300" data-id="element-2640">
                      <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2641">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2642" />
                      </svg>
                      <span data-id="element-2643">
                        <span className="font-semibold text-green-400" data-id="element-2644">
                          Lower upfront cost
                        </span>{' '}
                        — start for just $249
                      </span>
                    </li>
                  </ul>

                  <button onClick={showUpsell} className="block w-full px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black text-center font-bold text-base md:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 min-h-[44px] cursor-pointer" data-id="element-2645">
                    Apply Now — Monthly Membership
                  </button>
                </motion.div>
              </div>

              {/* Option 3: Alumni */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 6.8
            }} className="p-6 sm:p-8 bg-gradient-to-r from-purple-950/40 to-black border-2 border-purple-600/50 rounded-lg text-center" data-id="element-2646">
                <p className="text-purple-300 font-bold text-lg sm:text-xl mb-2" data-id="element-2647">
                  Already Own Peptide Mastery?
                </p>
                <p className="text-purple-200 mb-4 text-sm sm:text-base" data-id="element-2648">
                  Get special alumni pricing on the membership
                </p>
                <a href="mailto:support@support.peptidescienceinstitute.com" className="inline-block px-6 sm:px-8 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-lg transition-all duration-300 min-h-[44px]" data-id="element-2649">
                  Contact Us for Alumni Pricing
                </a>
              </motion.div>

              {/* BUT MAKE SURE YOU UNDERSTAND Section - MOVED HERE */}

              {/* THE DEADLINE Section */}
              <div className="border-t-2 border-amber-600/30 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-2650">
                <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.6,
                delay: 6.85
              }} className="text-center mb-6 sm:mb-8" data-id="element-2651">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4" style={{
                  textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
                }} data-id="element-2652">
                    ⏰ THE DEADLINE
                  </h3>
                  <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto" data-id="element-2653" />
                </motion.div>

                <div className="space-y-4 sm:space-y-6 max-w-3xl mx-auto text-center" data-id="element-2654">
                  <div className="p-6 sm:p-8 bg-gradient-to-r from-red-950/40 via-amber-950/40 to-red-950/40 border-2 border-amber-500/50 rounded-lg" data-id="element-2655">
                    <p className="text-xl sm:text-2xl font-bold text-amber-300 mb-3 sm:mb-4" data-id="element-2656">
                      This opportunity is only open until Friday, April 3rd at
                      11:59 PM ET
                    </p>
                    <p className="text-lg sm:text-xl text-stone-300 mb-2" data-id="element-2657">
                      After that, the doors close.
                    </p>
                  </div>
                </div>
              </div>

              {/* Michelle M.N. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 6.9
            }} className="relative mt-8" data-id="element-2658">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2659" />

                <div className="relative px-8 py-10" data-id="element-2660">
                  {/* Profile Image */}
                  <div className="flex justify-center mb-6" data-id="element-2661">
                    <div className="relative" data-id="element-2662">
                      <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2663" />
                      <img src="https://cdn.magicpatterns.com/uploads/9qMkPazyxjG9vTCAeui9mc/MichelleMN.jpg" alt="Michelle M.N." className="relative w-24 h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2664" />
                    </div>
                  </div>

                  {/* Large opening quote - purple */}
                  <div className="mb-6" data-id="element-2665">
                    <span className="text-purple-500/40 text-7xl font-serif leading-none" data-id="element-2666">
                      "
                    </span>
                  </div>

                  {/* TV Screen Frame around testimonial text */}
                  <div className="relative mb-8 max-w-3xl mx-auto" data-id="element-2667">
                    {/* Outer purple glow */}
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2668" />

                    {/* TV screen frame */}
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2669">
                      {/* Screen scanline effect (subtle) */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2670" />

                      {/* Testimonial text - with emphasis on key phrases */}
                      <p className="text-stone-300 text-lg md:text-xl leading-relaxed relative z-10" data-id="element-2671">
                        I'm going through the peptide mastery and all the other
                        peptide courses and resources I've purchased from you
                        guys{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2672">
                          (I am LOVING it all!!)
                        </span>
                        ... I'm a functional medicine practitioner and{' '}
                        <span className="text-white font-bold" data-id="element-2673">
                          I can't wait to incorporate some of these protocols
                          with my patients!!!
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Attribution with client type */}
                  <div className="flex items-center gap-4" data-id="element-2674">
                    <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2675" />
                    <div className="text-right" data-id="element-2676">
                      <p className="text-purple-400 font-semibold text-lg mb-1" data-id="element-2677">
                        Michelle M.N.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2678">
                        Doctor of Chiropractic & Certified Functional Medicine
                        Practitioner, Peptide Mastery buyer
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Tony R. Testimonial */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 7.1
            }} className="relative mt-16" data-id="element-2679">
                {/* Subtle background glow - purple tint */}
                <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent blur-2xl" data-id="element-2680" />

                <div className="relative px-8 py-10" data-id="element-2681">
                  {/* Profile Image */}
                  <div className="flex justify-center mb-6" data-id="element-2682">
                    <div className="relative" data-id="element-2683">
                      <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full" data-id="element-2684" />
                      <img src="https://cdn.magicpatterns.com/uploads/p7h6Y7wV2aZ8GFLXspCfFF/TonyR.jpg" alt="Tony R." className="relative w-24 h-24 rounded-full object-cover border-4 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-2685" />
                    </div>
                  </div>

                  {/* Large opening quote - purple */}
                  <div className="mb-6" data-id="element-2686">
                    <span className="text-purple-500/40 text-7xl font-serif leading-none" data-id="element-2687">
                      "
                    </span>
                  </div>

                  {/* TV Screen Frame around testimonial text */}
                  <div className="relative mb-8 max-w-3xl mx-auto" data-id="element-2688">
                    {/* Outer purple glow */}
                    <div className="absolute -inset-4 bg-purple-500/10 blur-xl rounded-lg" data-id="element-2689" />

                    {/* TV screen frame */}
                    <div className="relative border-2 border-purple-500/30 rounded-lg p-6 bg-gradient-to-br from-purple-950/20 to-black/40 shadow-[0_0_30px_rgba(168,85,247,0.2),inset_0_0_20px_rgba(168,85,247,0.05)]" data-id="element-2690">
                      {/* Screen scanline effect (subtle) */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none rounded-lg" data-id="element-2691" />

                      {/* Testimonial text - with emphasis on key phrases */}
                      <p className="text-stone-300 text-lg md:text-xl leading-relaxed relative z-10" data-id="element-2692">
                        I was honestly frustrated at first. All the questions,
                        testing and monitoring seemed excessive. But I
                        understand why it was so important. Everything gave you
                        information to fine-tune my program... What you have
                        managed to do is{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2693">
                          absolutely bonkers.
                        </span>{' '}
                        This is like a{' '}
                        <span className="text-white font-bold" data-id="element-2694">
                          whole different planet of health optimization that
                          most people don't even know exists
                        </span>
                        ... I've gone from{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-2695">
                          thinking about my funeral to planning hiking trips.
                        </span>{' '}
                        I can't thank you guys and the team enough.
                      </p>
                    </div>
                  </div>

                  {/* Attribution with client type */}
                  <div className="flex items-center gap-4" data-id="element-2696">
                    <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" data-id="element-2697" />
                    <div className="text-right" data-id="element-2698">
                      <p className="text-purple-400 font-semibold text-lg mb-1" data-id="element-2699">
                        Tony R.
                      </p>
                      <p className="text-stone-500 text-sm" data-id="element-2700">
                        Transformation Services Client
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* TRANSITION SECTION - "WANT THE FULL DETAILS?" */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 6.9
      }} className="mt-12 sm:mt-20 mb-10 sm:mb-16 text-center max-w-3xl mx-auto" data-id="element-2701">
          {/* Scroll icon */}
          <motion.div className="flex justify-center mb-6" animate={{
          y: [0, 8, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2702">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 border-2 border-amber-500/40 flex items-center justify-center" data-id="element-2703">
              <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-2704">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13l-7 7-7-7m14-8l-7 7-7-7" data-id="element-2705" />
              </svg>
            </div>
          </motion.div>

          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-4 sm:mb-6" style={{
          textShadow: '0 0 30px rgba(251, 191, 36, 0.4)'
        }} data-id="element-2706">
            📜 WANT THE FULL DETAILS?
          </h3>

          <div className="space-y-3 sm:space-y-4 text-stone-300" data-id="element-2707">
            <p className="text-lg sm:text-xl font-semibold" data-id="element-2708">
              Keep reading below.
            </p>

            <p className="text-base sm:text-lg" data-id="element-2709">
              I'm going to walk you through EXACTLY what you're getting...
            </p>

            <p className="text-base sm:text-lg" data-id="element-2710">
              Why this matters more now than ever...
            </p>

            <p className="text-base sm:text-lg font-bold text-amber-400" data-id="element-2711">
              And why this might be the most important decision you make all
              year.
            </p>
          </div>

          {/* Decorative divider */}
          <div className="flex items-center justify-center gap-4 mt-10" data-id="element-2712">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-2713" />
            <div className="w-2 h-2 bg-amber-500 rounded-full" data-id="element-2714" />
            <div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-2715" />
          </div>
        </motion.div>

        {/* Enhanced scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 5.0
      }} className="mt-16" data-id="element-2716">
          <div className="flex flex-col items-center gap-3" data-id="element-2717">
            <p className="text-amber-500 text-sm tracking-wider uppercase" data-id="element-2718">
              Discover More
            </p>
            <motion.div className="w-px h-16 bg-gradient-to-b from-amber-600 to-transparent" animate={{
            opacity: [0.3, 1, 0.3]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2719" />
            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2720">
              <ChevronDown className="w-6 h-6 text-amber-600" data-id="element-2721" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}